import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector, useDispatch } from "react-redux";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { setcurrent_cart } from "../../core/redux/action";
import timezones from "timezones.json";

import { useQuery, useMutation } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import {
  ShoppingCart,
  LayoutGrid,
  FileText,
  FileDown,
  Printer,
  Mail,
  RefreshCw,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isStoreChargingTax } from "@/lib/utils";
import { isItemWarrantyReturned } from "@/lib/warrantyUtils";
import {
  normalizeSerial,
  isSerialInCart,
  validateCartSerials,
  filterAvailableProductSerials,
} from "@/lib/posSerialUtils";

// Sub-components
import CustomerModal from "../../components/modals/CustomerModal";
import PaymentModal from "./components/pos/PaymentModal";
import RecentOrdersModal from "./components/pos/RecentOrdersModal";
import PaymentSuccessModal from "./components/pos/PaymentSuccessModal";
import FilterModal from "./components/pos/FilterModal";
import ItemConfigModal from "./components/pos/ItemConfigModal";
import InventorySelectorModal from "./components/pos/InventorySelectorModal";
import PinVerificationModal from "./components/pos/PinVerificationModal";
import ProductView from "./components/pos/ProductView";
import CartSidebar from "./components/pos/CartSidebar";
import PosActionsSidebar from "../pos-common/PosActionsSidebar";
import PosCreateWarrantyClaimModal from "../pos-common/PosCreateWarrantyClaimModal";

const Pos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [allProductsSearch, setAllProductsSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [init, setInit] = useState(false);
  const [initData, setInitData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCat, setCurrentCat] = useState(null);
  const [currentCatName, setCurrentCatName] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [products, setProducts] = useState([]);
  const [currentProducts, setCurrentProducts] = useState([]);

  const currentUser = useSelector((state) => state.user_data);
  const currentCart = useSelector((state) => state.current_cart);
  const currentStore = useSelector((state) => state.current_store?.id || null);
  const storeData = useSelector((state) => state.current_store);

  const getCurrentDateInStoreTimezone = () => {
    if (!storeData?.time_zone) {
      return new Date().toISOString();
    }

    // Parse the timezone string (format: "value-abbr")
    const [timezoneName] = storeData.time_zone.split("-");

    // Find the timezone data
    const timezoneData = timezones.find((tz) => tz.value === timezoneName);

    if (!timezoneData) {
      return new Date().toISOString();
    }

    // Calculate the current time in the store's timezone
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const storeTime = new Date(utcTime + timezoneData.offset * 3600000);

    return storeTime.toISOString();
  };

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  // Helper function to calculate tax based on new taxing system (Store & Product settings)
  const calculateItemTax = (discountedPrice, product) => {
    const dp = round2(discountedPrice);

    if (!isStoreChargingTax(storeData) || !product?.is_taxable) {
      return { taxAmount: 0, lineTotal: dp };
    }

    const taxRate = parseFloat(storeData.tax_percentage) || 0;
    const taxMethod = product.tax_method || "exclusive";

    if (taxMethod === "inclusive") {
      // Price already includes tax - no need to extract or show tax separately
      return { taxAmount: 0, lineTotal: dp };
    } else {
      // Tax is added on top - show tax separately, lineTotal stays pre-tax
      const taxAmount = round2((dp * taxRate) / 100);
      return { taxAmount, lineTotal: dp };
    }
  };

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentData, setPaymentData] = useState({
    sale_id: "",
    amount: 0,
    payment_method: "cash",
    transaction_id: "",
    payment_date: getCurrentDateInStoreTimezone(),
    authorization_code: "",
    notes: "",
    payment_status: "completed",
  });

  const [recentOrdersModalOpen, setRecentOrdersModalOpen] = useState(false);

  const [paymentSuccessModalOpen, setPaymentSuccessModalOpen] = useState(false);
  const [isClearCartDialogOpen, setIsClearCartDialogOpen] = useState(false);
  const [isClosePaymentDialogOpen, setIsClosePaymentDialogOpen] =
    useState(false);

  const [cart, setCart] = useState({
    id: null,
    customer_id: null,
    store_id: currentStore,
    user_id: currentUser?.id || null,
    subtotal: 0,
    tax_amount: 0,
    discount_type: "none",
    discount_amount: 0,
    discount_value: 0,
    item_count: 0,
    total: 0,
    status: "active",
    notes: null,
    custom_fields: null,
    items: [],
  });

  // Keep cart user_id in sync if currentUser loads late
  useEffect(() => {
    if (currentUser?.id && !cart.user_id) {
      setCart((prev) => ({ ...prev, user_id: currentUser.id }));
    }
  }, [currentUser, cart.user_id]);

  const dispatch = useDispatch();

  const [inProgress, setInProgress] = useState(false);
  const [taxes, setTaxes] = useState([]);

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [receiptGenerating, setReceiptGenerating] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [currentSaleData, setCurrentSaleData] = useState(null);
  const [warrantyClaimModalOpen, setWarrantyClaimModalOpen] = useState(false);
  const [selectedWarrantyItem, setSelectedWarrantyItem] = useState(null);

  const balanceDue = useMemo(() => {
    if (!currentSaleData) return 0;
    const pStatus = (currentSaleData.payment_status || "").toLowerCase();
    if (pStatus === "completed" || pStatus === "paid") return 0;
    const totalVal = parseFloat(currentSaleData.total || currentSaleData.total_amount || 0);
    let paid = parseFloat(paidAmount || currentSaleData.paid_amount || 0);
    if (!paid && Array.isArray(currentSaleData.payments) && currentSaleData.payments.length > 0) {
      paid = currentSaleData.payments.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
    }
    if (currentSaleData.balance_due != null && (!payments || payments.length === 0)) {
      return parseFloat(currentSaleData.balance_due);
    }
    return Math.max(0, parseFloat((totalVal - paid).toFixed(2)));
  }, [currentSaleData, paidAmount, payments]);
  const [pdfError, setPdfError] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [payLaterLoading, setPayLaterLoading] = useState(false);
  const [completeSaleLoading, setCompleteSaleLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const updateInProgress = useRef(false);
  const quantityUpdateTimers = useRef({});
  const allProductsSearchTimeoutRef = useRef(null);
  const categorySearchTimeoutRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastLoadedCartRef = useRef(null);
  const updateCartItemQuantityRef = useRef(null);
  const cartSyncTimerRef = useRef(null);
  const activeCartSessionRef = useRef(0);

  const [buttonLoading, setButtonLoading] = useState(false);

  const permissions = useSelector((state) => state.userpermission_data);

  const hasPermission = React.useCallback(
    (permission) => {
      const perms = permissions || [];
      return perms && perms.includes(permission);
    },
    [permissions],
  );

  const [activeTab, setActiveTab] = useState("by-category");

  const [productsLoading, setProductsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [productsError, setProductsError] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);

  const [customerSearchExpanded, setCustomerSearchExpanded] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [showCartOnMobile, setShowCartOnMobile] = useState(true);
  const [priceFilter, setPriceFilter] = useState({ min: "", max: "" });

  // Recent products state
  const [recentProducts, setRecentProducts] = useState(() => {
    const saved = localStorage.getItem("pos_recent_products");
    return saved ? JSON.parse(saved) : [];
  });

  const updateRecentProducts = (product) => {
    setRecentProducts((prev) => {
      const filtered = prev.filter((p) => p.id !== product.id);
      const updated = [product, ...filtered].slice(0, 8); // Keep last 8 products
      localStorage.setItem("pos_recent_products", JSON.stringify(updated));
      return updated;
    });
  };

  // Toast helper function
  const showToast = (message, type) => {
    if (type === "success") {
      sonnerToast.success(message);
    } else if (type === "error") {
      sonnerToast.error(message);
    } else if (type === "warning") {
      sonnerToast.warning(message);
    } else {
      sonnerToast(message);
    }
  };

  const handleProtectedAction = (action) => {
    // PIN verification disabled for now
    action();

    // Commented out to disable PIN verification as per user request
    /*
    setPendingAction(() => action);
    // setPinActionTitle(title || "Action");
    setPinValue("");
    setPinModalOpen(true);
    */
  };

  const handleVerifyPin = async (e) => {
    if (e) e.preventDefault();
    if (pinValue.length < 4 || pinValue.length > 6) {
      showToast("PIN must be 4-6 characters", "error");
      return;
    }

    setPinVerifying(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.USERS.VERIFY_PIN, {
        employee_id: currentUser?.employee?.id || null,
        store_id: currentStore,
        access_pin: pinValue,
      });

      if (response.data.verified) {
        setPinModalOpen(false);
        if (pendingAction) {
          pendingAction();
        }
        setPendingAction(null);
        setPinValue("");
      } else {
        showToast(response.data.message || "Invalid PIN", "error");
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Verification failed",
        "error",
      );
    } finally {
      setPinVerifying(false);
    }
  };

  const completing = false;
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Item loading state for quantity updates
  const [updatingItemIds, setUpdatingItemIds] = useState([]);
  const [qtyUpdating, setQtyUpdating] = useState(false);

  // Local quantity state for real-time display
  const [localQuantities, setLocalQuantities] = useState({});

  // Item modal states
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);

  // Inventory selector states
  const [showInventorySelector, setShowInventorySelector] = useState(false);
  const [inventorySelectorType, setInventorySelectorType] = useState("none");
  const [inventorySelectorItems, setInventorySelectorItems] = useState([]);
  const [inventorySelectorProduct, setInventorySelectorProduct] =
    useState(null);
  const [inventorySelectorQuantity, setInventorySelectorQuantity] = useState(1);

  const fetchAvailableSerials = async (product) => {
    // Product already contains available serial numbers
    const serials = product.serial_numbers || [];
    setAvailableSerials(serials);
    return serials;
  };

  const fetchAvailableBatches = async (product) => {
    // Product already contains available batches
    const batches = product.batches || [];
    setAvailableBatches(batches);
    return batches;
  };

  const [currentItemConfig, setCurrentItemConfig] = useState({
    product_id: null,
    cart_id: null,
    quantity: 1,
    original_price: 0,
    unit_price: 0,
    line_total: 0,
    discount_amount: 0,
    discount_type: "none",
    discount_value: 0,
    tax_amount: 0,
    tax_id: null,
    notes: "",
    product_data: null,
    serial_numbers: [],
    inventory_batch_id: null,
  });
  const [selectedModalProduct, setSelectedModalProduct] = useState(null);

  // Pin verification states
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [pinVerifying, setPinVerifying] = useState(false);
  const [pinActionTitle] = useState("");

  const discountTypeOptions = [
    { value: "percentage", label: "Percentage (%)" },
    { value: "fixed", label: "Fixed Amount" },
  ];

  const taxOptions = taxes.map((tax) => ({
    value: tax.id,
    label: `${tax.name} (${tax.type === "percentage"
      ? `${tax.amount}%`
      : `${currencySymbol}${tax.amount}`
      })`,
  }));

  const { data: posData, isError: posError } = useQuery({
    queryKey: ["pos-init"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.POS_INIT);
      return res.data;
    },
    staleTime: Infinity,
    enabled: !init,
  });

  // Fetch payment methods available for sales scope
  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "sales", currentStore],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: currentStore || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: hasPermission("payment-method read"),
  });

  const { data: posCategoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["pos-categories-all"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: { perPage: 9999 },
      });
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Query child categories when user is inside a category using parent_id and debounced search query params
  const {
    data: posSubcategoriesData,
    isLoading: subcategoriesLoading,
  } = useQuery({
    queryKey: ["pos-subcategories", currentCat, debouncedCategorySearch],
    queryFn: async () => {
      if (!currentCat || currentCat === "all") return [];
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: {
          parent_id: currentCat,
          search: debouncedCategorySearch || undefined,
          perPage: 100,
        },
      });
      return res.data;
    },
    enabled:
      Boolean(currentCat) &&
      currentCat !== "all" &&
      hasPermission("category product read"),
    staleTime: 5 * 60 * 1000,
  });

  // If current category is not in the categories list, fetch its details to resolve its parent_id for Back navigation
  const { data: currentCategoryDetails } = useQuery({
    queryKey: ["category-details", currentCat],
    queryFn: async () => {
      if (!currentCat || currentCat === "all") return null;
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.CATEGORY_DETAILS(currentCat),
      );
      return res.data?.data || res.data || null;
    },
    enabled:
      Boolean(currentCat) &&
      currentCat !== "all" &&
      !categories.some((c) => String(c.id) === String(currentCat)) &&
      hasPermission("category product read"),
    staleTime: 5 * 60 * 1000,
  });

  const formatDateTime = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return d.toISOString();
      }
    } catch {
      // ignore
    }
    return String(val);
  };

  const formatCartPayload = useCallback(
    (cartData, items = [], overrides = {}) => {
      let subtotal = 0;
      let taxAmount = 0;
      let itemCount = 0;

      const rawItems = items || [];
      const formattedItems = rawItems.map((item) => {
        // Resolve product object to look up serials/batches by ID if necessary
        let productObj = null;
        if (typeof item.product_data === "object" && item.product_data !== null) {
          productObj = item.product_data;
        } else if (
          typeof item.product_data === "string" &&
          item.product_data.trim() !== ""
        ) {
          try {
            productObj = JSON.parse(item.product_data);
          } catch {
            productObj = null;
          }
        }
        if (!productObj && item.product && typeof item.product === "object") {
          productObj = item.product;
        }

        // 1. Gather all potential serial numbers
        let rawSerials = [];

        if (Array.isArray(item.serial_numbers) && item.serial_numbers.length > 0) {
          rawSerials = item.serial_numbers;
        } else if (
          typeof item.serial_numbers === "string" &&
          item.serial_numbers.trim() !== "" &&
          item.serial_numbers !== "null"
        ) {
          try {
            const parsed = JSON.parse(item.serial_numbers);
            if (Array.isArray(parsed) && parsed.length > 0) {
              rawSerials = parsed;
            } else if (typeof parsed === "string" && parsed.trim() !== "") {
              rawSerials = [parsed.trim()];
            }
          } catch {
            rawSerials = [item.serial_numbers.trim()];
          }
        }

        // Check top-level serial_number
        if (
          rawSerials.length === 0 &&
          item.serial_number &&
          typeof item.serial_number === "string" &&
          item.serial_number.trim() !== "" &&
          item.serial_number !== "null"
        ) {
          rawSerials = [item.serial_number.trim()];
        }

        // Check eager-loaded inventory_serial object
        if (
          rawSerials.length === 0 &&
          item.inventory_serial &&
          typeof item.inventory_serial === "object" &&
          item.inventory_serial.serial_number
        ) {
          rawSerials = [item.inventory_serial.serial_number];
        }

        // Check eager-loaded inventory_serials (array or object)
        if (rawSerials.length === 0 && item.inventory_serials) {
          if (Array.isArray(item.inventory_serials)) {
            rawSerials = item.inventory_serials
              .map((s) => (typeof s === "object" && s !== null ? s.serial_number : s))
              .filter(Boolean);
          } else if (item.inventory_serials?.serial_number) {
            rawSerials = [item.inventory_serials.serial_number];
          }
        }

        // If still empty but inventory_serial_id exists, match in productObj.serial_numbers
        const targetSerialId =
          item.inventory_serial_id || item.inventory_serial?.id || null;
        if (
          rawSerials.length === 0 &&
          targetSerialId &&
          productObj &&
          Array.isArray(productObj.serial_numbers)
        ) {
          const matched = productObj.serial_numbers.find(
            (s) => s.id === targetSerialId,
          );
          if (matched?.serial_number) {
            rawSerials = [matched.serial_number];
          }
        }

        // Clean, normalize and deduplicate as strings
        const serialStrings = Array.from(
          new Set(rawSerials.map(normalizeSerial).filter(Boolean)),
        );

        const serialNumbers = serialStrings.length > 0 ? serialStrings : null;
        const singleSerial =
          serialStrings.length > 0
            ? serialStrings[0]
            : (item.serial_number
              ? normalizeSerial(item.serial_number)
              : item.inventory_serial?.serial_number
                ? normalizeSerial(item.inventory_serial.serial_number)
                : null);

        // 2. Resolve inventory_batch_id
        let inventoryBatchId =
          item.inventory_batch_id || item.inventory_batch?.id || null;
        const batchNum =
          item.batch_number || item.inventory_batch?.batch_number || null;
        if (
          !inventoryBatchId &&
          batchNum &&
          productObj &&
          Array.isArray(productObj.batches)
        ) {
          const matched = productObj.batches.find(
            (b) => b.batch_number === batchNum || b.id === batchNum,
          );
          if (matched?.id) {
            inventoryBatchId = matched.id;
          }
        }

        const discountType =
          item.discount_type === "percentage" || item.discount_type === "fixed"
            ? item.discount_type
            : "none";

        let productDataStr = null;
        if (item.product_data) {
          productDataStr =
            typeof item.product_data === "object"
              ? JSON.stringify(item.product_data)
              : String(item.product_data);
        } else if (item.product) {
          productDataStr =
            typeof item.product === "object"
              ? JSON.stringify(item.product)
              : String(item.product);
        }

        let customFieldsStr = null;
        if (item.custom_fields) {
          customFieldsStr =
            typeof item.custom_fields === "object"
              ? JSON.stringify(item.custom_fields)
              : String(item.custom_fields);
        }

        const productId =
          item.product_id ||
          item.product?.id ||
          productObj?.id ||
          (item.id && !String(item.id).startsWith("temp-") ? item.id : null);

        const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
        const unitPrice =
          parseFloat(
            parseFloat(
              item.unit_price != null
                ? item.unit_price
                : (productObj?.sell_price != null ? productObj.sell_price : 0),
            ).toFixed(2),
          ) || 0;
        const origPrice =
          parseFloat(
            parseFloat(
              item.original_price != null ? item.original_price : unitPrice,
            ).toFixed(2),
          ) || 0;
        const discAmount =
          parseFloat(parseFloat(item.discount_amount || 0).toFixed(2)) || 0;
        const discVal =
          parseFloat(parseFloat(item.discount_value || 0).toFixed(2)) || 0;
        const itemTax =
          parseFloat(parseFloat(item.tax_amount || 0).toFixed(2)) || 0;
        const taxPercentage =
          parseFloat(parseFloat(item.tax_percentage || 0).toFixed(2)) || 0;

        const rawLineTotal =
          item.line_total != null
            ? parseFloat(item.line_total)
            : unitPrice * qty - discAmount;
        const lineTotal =
          parseFloat(
            Math.max(0, isNaN(rawLineTotal) ? 0 : rawLineTotal).toFixed(2),
          ) || 0;

        subtotal += lineTotal;
        taxAmount += itemTax;
        itemCount += 1;

        return {
          product_id: productId,
          parent_id: item.parent_id || null,
          inventory_batch_id: inventoryBatchId,
          serial_numbers: serialNumbers,
          serial_number: singleSerial,
          quantity: qty,
          unit_price: unitPrice,
          original_price: origPrice,
          discount_amount: discAmount,
          discount_type: discountType,
          discount_value: discVal,
          tax_amount: itemTax,
          tax_class: item.tax_class || item.tax_id || null,
          tax_percentage: taxPercentage,
          line_total: lineTotal,
          notes: item.notes || null,
          product_data: productDataStr,
          custom_fields: customFieldsStr,
          warranty_start_date: formatDateTime(item.warranty_start_date),
          warranty_end_date: formatDateTime(item.warranty_end_date),
          warranty_notes: item.warranty_notes || null,
        };
      });

      const targetDiscountType =
        overrides.discount_type !== undefined
          ? overrides.discount_type
          : cartData.discount_type;

      const cartDiscountType =
        targetDiscountType === "percentage" || targetDiscountType === "fixed"
          ? targetDiscountType
          : "none";

      const cartDiscountVal =
        parseFloat(
          overrides.discount_value !== undefined
            ? overrides.discount_value
            : cartData.discount_value,
        ) || 0;

      let cartDiscountAmount = 0;
      if (cartDiscountType === "percentage" && cartDiscountVal > 0) {
        cartDiscountAmount = (subtotal * cartDiscountVal) / 100;
      } else if (cartDiscountType === "fixed" && cartDiscountVal > 0) {
        cartDiscountAmount = cartDiscountVal;
      }

      const finalSubtotal = parseFloat((subtotal || 0).toFixed(2)) || 0;
      const finalTax = parseFloat((taxAmount || 0).toFixed(2)) || 0;
      const finalDiscount =
        parseFloat((cartDiscountAmount || 0).toFixed(2)) || 0;
      const finalDiscountVal =
        parseFloat((cartDiscountVal || 0).toFixed(2)) || 0;
      const finalTotal =
        parseFloat(
          Math.max(0, finalSubtotal - finalDiscount + finalTax).toFixed(2),
        ) || 0;
      const finalItemCount = parseInt(itemCount, 10) || 0;

      let rawStatus = overrides.status || cartData.status || "active";
      if (rawStatus === "on_hold") rawStatus = "on-hold";

      const payload = {
        customer_id:
          overrides.customer_id ||
          cartData.customer_id ||
          customer?.id ||
          initData?.walk_in_customer?.id,
        store_id: overrides.store_id || currentStore || cartData.store_id,
        user_id: overrides.user_id || currentUser?.id || cartData.user_id,
        subtotal: finalSubtotal,
        tax_amount: finalTax,
        discount_amount: finalDiscount,
        discount_type: cartDiscountType,
        discount_value: finalDiscountVal,
        total: finalTotal,
        item_count: finalItemCount,
        status: rawStatus,
        notes:
          overrides.notes !== undefined
            ? overrides.notes
            : cartData.notes || null,
        custom_fields: Array.isArray(cartData.custom_fields)
          ? cartData.custom_fields
          : null,
      };

      payload.items = formattedItems;

      return {
        payload,
        completeCartState: {
          ...cartData,
          ...payload,
          id: overrides.id || cartData.id,
          discount_type: cartDiscountType,
          items: Array.isArray(items) ? items : (cartData.items || []),
        },
      };
    }, [currentStore, currentUser?.id, customer?.id, initData?.walk_in_customer?.id]);

  const createCartMutation = useMutation({
    mutationFn: async ({ customer_id, items = [], overrides = {} }) => {
      const session = activeCartSessionRef.current;
      const customerId =
        customer_id ||
        cart.customer_id ||
        customer?.id ||
        initData?.walk_in_customer?.id;

      const { isValid, duplicates } = validateCartSerials(items);
      if (!isValid) {
        const errorMsg = `Cannot submit cart: Duplicate serial number(s) detected: ${duplicates.join(", ")}`;
        showToast(errorMsg, "error");
        throw new Error(errorMsg);
      }

      const { payload } = formatCartPayload(
        cart,
        items,
        {
          customer_id: customerId,
          store_id: currentStore || cart.store_id,
          user_id: currentUser?.id || cart.user_id,
          status: "active",
          ...overrides,
        },
      );

      const res = await apiClient.post(
        API_ENDPOINTS.SALES.POS_CART,
        payload,
      );
      return { data: res.data?.data || res.data, session };
    },
    onSuccess: ({ data, session }) => {
      if (activeCartSessionRef.current !== session) return;
      setCart(data);
      dispatch(setcurrent_cart(data));
      setInProgress(true);
      lastLoadedCartRef.current = String(data.id);
      if (Array.isArray(data.items) && data.items.length > 0) {
        setCurrentProducts(data.items);
      } else if (data?.id) {
        fetchCartItems(data.id);
      }
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || error.message || "Failed to create cart",
        "error",
      );
    },
  });

  useEffect(() => {
    if (posData) {
      setInit(true);
      setInitData(posData);

      if (posData.taxes) {
        setTaxes(posData.taxes);
      }

      if (posData.walk_in_customer && !customer) {
        setCustomer(posData.walk_in_customer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posData]);

  useEffect(() => {
    const rawCategories = Array.isArray(posCategoriesData)
      ? posCategoriesData
      : Array.isArray(posCategoriesData?.data)
        ? posCategoriesData.data
        : [];

    if (rawCategories.length > 0) {
      const isCatActive = (c) =>
        c.is_active === true ||
        c.is_active === 1 ||
        c.is_active === "1" ||
        c.is_active === "true" ||
        c.is_active === undefined ||
        c.is_active === null;

      const isCatFeatured = (c) =>
        Boolean(
          c.is_featured === true ||
          c.is_featured === 1 ||
          c.is_featured === "1" ||
          c.is_featured === "true",
        );

      const activeCategories = rawCategories.filter(isCatActive);

      const sortedCategories = [...activeCategories].sort((a, b) => {
        const aFeatured = isCatFeatured(a);
        const bFeatured = isCatFeatured(b);

        // Featured categories prioritized first
        if (aFeatured !== bFeatured) {
          return bFeatured ? 1 : -1;
        }

        // Within same featured tier, preserve category list sort_order
        const aOrder =
          a.sort_order !== null && a.sort_order !== undefined
            ? Number(a.sort_order)
            : 0;
        const bOrder =
          b.sort_order !== null && b.sort_order !== undefined
            ? Number(b.sort_order)
            : 0;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return (Number(a.id) || 0) - (Number(b.id) || 0);
      });

      setCategories(sortedCategories);
    } else if (posCategoriesData) {
      setCategories([]);
    }
  }, [posCategoriesData]);

  // Process subcategories for the current category
  const subcategories = useMemo(() => {
    if (!currentCat || currentCat === "all") return [];

    const rawSubcategories = Array.isArray(posSubcategoriesData)
      ? posSubcategoriesData
      : Array.isArray(posSubcategoriesData?.data)
        ? posSubcategoriesData.data
        : [];

    const isCatActive = (c) =>
      c.is_active === true ||
      c.is_active === 1 ||
      c.is_active === "1" ||
      c.is_active === "true" ||
      c.is_active === undefined ||
      c.is_active === null;

    const isCatFeatured = (c) =>
      Boolean(
        c.is_featured === true ||
        c.is_featured === 1 ||
        c.is_featured === "1" ||
        c.is_featured === "true",
      );

    if (rawSubcategories.length > 0) {
      const activeCategories = rawSubcategories.filter(isCatActive);

      const filterTerm = (debouncedCategorySearch || categorySearch || "").trim().toLowerCase();
      const filtered = filterTerm
        ? activeCategories.filter((c) =>
          c.name?.toLowerCase().includes(filterTerm),
        )
        : activeCategories;

      return [...filtered].sort((a, b) => {
        const aFeatured = isCatFeatured(a);
        const bFeatured = isCatFeatured(b);

        if (aFeatured !== bFeatured) {
          return bFeatured ? 1 : -1;
        }

        const aOrder =
          a.sort_order !== null && a.sort_order !== undefined
            ? Number(a.sort_order)
            : 0;
        const bOrder =
          b.sort_order !== null && b.sort_order !== undefined
            ? Number(b.sort_order)
            : 0;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return (Number(a.id) || 0) - (Number(b.id) || 0);
      });
    }

    // Fallback: If query returned no categories or hasn't finished, check in-memory categories
    if (!subcategoriesLoading && categories.length > 0) {
      const inMemoryChildren = categories.filter(
        (c) => String(c.parent_id) === String(currentCat),
      );
      const filterTerm = (debouncedCategorySearch || categorySearch || "").trim().toLowerCase();
      if (filterTerm) {
        return inMemoryChildren.filter((c) =>
          c.name?.toLowerCase().includes(filterTerm),
        );
      }
      return inMemoryChildren;
    }

    return [];
  }, [
    posSubcategoriesData,
    currentCat,
    debouncedCategorySearch,
    categorySearch,
    subcategoriesLoading,
    categories,
  ]);

  // Keep all discovered subcategories in the main categories list so navigation and parent lookups work seamlessly
  useEffect(() => {
    if (subcategories && subcategories.length > 0) {
      setCategories((prevCategories) => {
        const existingIds = new Set(prevCategories.map((c) => String(c.id)));
        const newItems = subcategories.filter(
          (c) => !existingIds.has(String(c.id)),
        );
        if (newItems.length === 0) return prevCategories;
        return [...prevCategories, ...newItems];
      });
    }
  }, [subcategories]);

  // If category details were fetched directly, add to categories cache and update name
  useEffect(() => {
    if (currentCategoryDetails) {
      setCategories((prev) => {
        if (
          prev.some((c) => String(c.id) === String(currentCategoryDetails.id))
        ) {
          return prev;
        }
        return [...prev, currentCategoryDetails];
      });
      if (!currentCatName && currentCategoryDetails.name) {
        setCurrentCatName(currentCategoryDetails.name);
      }
    }
  }, [currentCategoryDetails, currentCatName]);

  useEffect(() => {
    if (posError) {
      showToast("Failed to load POS", "error");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [posError]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const searchStr = window.location.search || location.search;
    const searchParams = new URLSearchParams(searchStr);
    const cartId = searchParams.get("cartId");

    if (cartId) {
      if (lastLoadedCartRef.current === cartId) return;
      lastLoadedCartRef.current = cartId;
      fetchCart(cartId);
    } else if (currentCart && currentCart.id && !inProgress) {
      const id = String(currentCart.id);
      if (lastLoadedCartRef.current === id) return;
      lastLoadedCartRef.current = id;
      fetchCart(currentCart.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCart, location.search]);

  const fetchCart = async (cart_id) => {
    const session = activeCartSessionRef.current;
    if (!hasPermission("cart read")) {
      showToast("You don't have permission to read cart details", "error");
      return;
    }
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.SALES.POS_CART_DETAILS(cart_id),
      );
      if (activeCartSessionRef.current !== session) return;
      if (res.status === 200 || res.status === 201) {
        const cartData = res.data?.data || res.data;
        if (cartData && cartData.id) {
          if (cartData.status === "cancelled") {
            return;
          }
          setCart(cartData);
          if (cartData.customer) {
            setCustomer(cartData.customer);
          }
          if (Array.isArray(cartData.items) && cartData.items.length > 0) {
            setCurrentProducts(cartData.items);
          } else {
            fetchCartItems(cart_id);
          }
          dispatch(setcurrent_cart(cartData));
          setInProgress(true);
        }
      }
    } catch (error) {
      console.log(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch cart",
      );
    }
  };

  const fetchCartItems = async (cart_id) => {
    const session = activeCartSessionRef.current;
    if (!hasPermission("cart item read")) {
      return [];
    }
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.SALES.POS_CART_ITEMS(cart_id),
      );
      if (activeCartSessionRef.current !== session) return [];
      if (res.status === 200 || res.status === 201) {
        const items = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
        setCurrentProducts(items);

        // Clean up local quantities for items no longer in cart
        const currentItemIds = items.map((item) => item.id);
        setLocalQuantities((prev) => {
          const updated = {};
          Object.keys(prev).forEach((key) => {
            if (currentItemIds.includes(parseInt(key))) {
              updated[key] = prev[key];
            }
          });
          return updated;
        });
        return items;
      }
      return [];
    } catch (error) {
      console.log(
        error.response?.data?.message || "Failed to fetch cart items",
      );
      return [];
    }
  };

  const createCart = async (customerIdOrObj, initialItems = [], overrides = {}) => {
    if (!hasPermission("cart create")) {
      showToast("You don't have permission to create a cart", "error");
      return null;
    }

    let targetCustomerId;
    let itemsToUse = initialItems;
    let extraOverrides = overrides;

    if (typeof customerIdOrObj === "object" && customerIdOrObj !== null) {
      targetCustomerId = customerIdOrObj.customer_id;
      itemsToUse = customerIdOrObj.items || initialItems;
      extraOverrides = customerIdOrObj.overrides || overrides;
    } else {
      targetCustomerId = customerIdOrObj;
    }

    targetCustomerId =
      targetCustomerId ||
      cart.customer_id ||
      customer?.id ||
      initData?.walk_in_customer?.id;

    if (!targetCustomerId) {
      showToast("Customer ID is required to create a cart", "error");
      return null;
    }

    // Only create cart on backend when there are items
    if (!itemsToUse || itemsToUse.length === 0) {
      setCart((prev) => ({
        ...prev,
        customer_id: targetCustomerId,
        ...extraOverrides,
      }));
      return null;
    }

    const result = await createCartMutation.mutateAsync({
      customer_id: targetCustomerId,
      items: itemsToUse,
      overrides: extraOverrides,
    });
    return result?.data || result;
  };

  const updateCart = useCallback(
    async (data, customItems = null) => {
      const cartId = data?.id || cart.id;
      if (currentSaleData || (cartId && String(cartId).startsWith("sale-"))) {
        return;
      }
      if (!hasPermission("cart update")) {
        showToast("You don't have permission to update cart", "error");
        return;
      }
      if (!cartId || updateInProgress.current) return;

      const session = activeCartSessionRef.current;
      try {
        updateInProgress.current = true;
        const itemsToUse =
          customItems !== null ? customItems : currentProducts;

        const { isValid, duplicates } = validateCartSerials(itemsToUse);
        if (!isValid) {
          showToast(
            `Cannot update cart: Duplicate serial number(s) detected: ${duplicates.join(", ")}`,
            "error",
          );
          return;
        }

        const { payload, completeCartState } = formatCartPayload(
          { ...cart, ...data },
          itemsToUse,
          {
            id: cartId,
            ...data,
          },
        );

        const res = await apiClient.put(
          API_ENDPOINTS.SALES.POS_CART_DETAILS(cartId),
          payload,
        );

        if (activeCartSessionRef.current !== session) return;

        const serverCart = res.data?.data || completeCartState;
        const finalCart = {
          ...serverCart,
          items: Array.isArray(serverCart.items)
            ? (itemsToUse && itemsToUse.length === 0 ? [] : serverCart.items)
            : (itemsToUse || []),
        };
        setCart(finalCart);
        dispatch(setcurrent_cart(finalCart));
        if (Array.isArray(finalCart.items) && finalCart.items.length > 0) {
          setCurrentProducts(finalCart.items);
        } else if (itemsToUse && itemsToUse.length === 0) {
          setCurrentProducts([]);
        }
        return finalCart;
      } catch (error) {
        if (activeCartSessionRef.current !== session) return;
        console.error(error.response?.data?.message || "Failed to update cart");
        showToast(error.response?.data?.message || "Failed to update cart", "error");
      } finally {
        updateInProgress.current = false;
      }
    },
    [cart, currentProducts, currentSaleData, dispatch, formatCartPayload, hasPermission],
  );

  const addCartItem = async (product, quantity = 1, scannedCode = null) => {
    if (currentSaleData) {
      showToast(
        "This sale is finalized and read-only. Exit sale view to start a new cart.",
        "warning",
      );
      return;
    }
    if (!hasPermission("cart item create")) {
      showToast("You don't have permission to add items to cart", "error");
      return;
    }
    // Check for tracking requirements (batches/serials)
    const trackingType = product.manage_inventory
      ? product.tracking_type || (product.is_serialized ? "serial" : "none")
      : "none";

    // Check inventory if manage_inventory is enabled
    if (product.manage_inventory) {
      const availableQty = parseFloat(product.sellable_qty || 0);
      if (availableQty <= 0) {
        showToast(
          "This product is out of stock and cannot be added to the cart",
          "error",
        );
        return;
      }
    }

    if (trackingType !== "none") {
      let items =
        trackingType === "standard"
          ? product.batches || []
          : product.serial_numbers || [];

      if (trackingType === "serial") {
        // If a specific serial code was scanned/passed, check if it's already in the cart
        if (scannedCode) {
          const trimmedScanned = normalizeSerial(scannedCode);
          if (isSerialInCart(currentProducts, trimmedScanned)) {
            showToast(
              `Serial number "${trimmedScanned}" is already in the cart`,
              "error",
            );
            return;
          }
        }

        // Filter out serial numbers already in the cart and deduplicate
        items = filterAvailableProductSerials(items, currentProducts);

        if (scannedCode && quantity === 1) {
          const target = normalizeSerial(scannedCode).toLowerCase();
          let matchedSerial = items.find(
            (item) => normalizeSerial(item).toLowerCase() === target,
          );
          if (!matchedSerial) {
            const partialMatches = items.filter((item) =>
              normalizeSerial(item).toLowerCase().includes(target),
            );
            if (partialMatches.length > 0) {
              matchedSerial = partialMatches[0];
            }
          }
          if (matchedSerial) {
            const actualSerial = normalizeSerial(matchedSerial);
            return finishAddingToCart(product, quantity, {
              inventory_batch_id: null,
              serial_numbers: [actualSerial],
            });
          } else {
            // Check if this was one of the product's serials that is ALREADY in the cart
            const allProductSerials = (product.serial_numbers || []).map(normalizeSerial);
            if (allProductSerials.some((s) => s.toLowerCase() === target)) {
              showToast(
                `Serial number "${scannedCode}" is already in the cart`,
                "error",
              );
              return;
            }
          }
        }
      }

      if (items.length === 0) {
        const hasOriginalItems =
          trackingType === "standard"
            ? product.batches?.length > 0
            : product.serial_numbers?.length > 0;

        showToast(
          hasOriginalItems
            ? `All available ${trackingType === "standard" ? "batches" : "serial numbers"} are already in the cart`
            : `No available ${trackingType === "standard" ? "batches" : "serial numbers"} for this product`,
          "error",
        );
        return;
      }

      // If single item, we can auto-assign
      if (items.length === 1 && quantity === 1) {
        const autoBatchId = trackingType === "standard" ? items[0].id : null;
        const autoSerials =
          trackingType === "serial"
            ? [normalizeSerial(items[0])]
            : [];

        if (
          trackingType === "serial" &&
          autoSerials.length > 0 &&
          isSerialInCart(currentProducts, autoSerials[0])
        ) {
          showToast(
            `Serial number "${autoSerials[0]}" is already in the cart`,
            "error",
          );
          return;
        }

        // Continue with automatic add
        return finishAddingToCart(product, quantity, {
          inventory_batch_id: autoBatchId,
          serial_numbers: autoSerials,
        });
      }

      // If multiple items, open the dedicated selection modal
      setInventorySelectorProduct(product);
      setInventorySelectorType(trackingType);
      setInventorySelectorItems(items);
      setInventorySelectorQuantity(quantity);
      setShowInventorySelector(true);
      return;
    }

    // No tracking - proceed directly
    return finishAddingToCart(product, quantity);
  };

  const finishAddingToCart = async (product, quantity, config = {}) => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    // Update recent products
    updateRecentProducts(product);

    let warrantyStart = config.warranty_start_date || null;
    let warrantyEnd = config.warranty_end_date || null;
    let warrantyNotes = config.warranty_notes || null;

    if (
      !warrantyStart &&
      product.warranty_timeframe &&
      product.warranty_timeframe !== "No Warranty" &&
      product.warranty > 0
    ) {
      const now = new Date();
      warrantyStart = now.toISOString().split("T")[0];
      const endDate = new Date(now);
      if (product.warranty_timeframe === "Days")
        endDate.setDate(endDate.getDate() + parseInt(product.warranty));
      else if (product.warranty_timeframe === "Months")
        endDate.setMonth(endDate.getMonth() + parseInt(product.warranty));
      else if (product.warranty_timeframe === "Years")
        endDate.setFullYear(endDate.getFullYear() + parseInt(product.warranty));
      else if (product.warranty_timeframe === "Lifetime")
        endDate.setFullYear(endDate.getFullYear() + parseInt(product.warranty));
      warrantyEnd = endDate.toISOString().split("T")[0];
    }

    const rawSerialNumbers = config.serial_numbers || [];
    const serialNumbers = rawSerialNumbers
      .map(normalizeSerial)
      .filter(Boolean);

    if (serialNumbers.length > 0) {
      // 1. Check for duplicates within this selection itself
      const seenBatch = new Set();
      for (const sn of serialNumbers) {
        const lower = sn.toLowerCase();
        if (seenBatch.has(lower)) {
          showToast(`Duplicate serial number "${sn}" cannot be added`, "error");
          return;
        }
        seenBatch.add(lower);
      }

      // 2. Check if any serial is already in the cart
      for (const sn of serialNumbers) {
        if (isSerialInCart(currentProducts, sn)) {
          showToast(`Serial number "${sn}" is already in the cart`, "error");
          return;
        }
      }
    }

    let newItems = [];

    // If there are serial numbers, add each as a separate cart item with quantity 1
    if (serialNumbers.length > 0) {
      newItems = serialNumbers.map((serial) => {
        const { taxAmount, lineTotal } = calculateItemTax(
          product.sell_price,
          product,
          1,
        );
        const matchedSerialObj = Array.isArray(product.serial_numbers)
          ? product.serial_numbers.find((s) => {
            const sn =
              typeof s === "string"
                ? s
                : s?.serial_number || s?.number || s?.serial || "";
            return sn === serial || s?.id === serial;
          })
          : null;
        const serialId =
          typeof matchedSerialObj === "object" ? matchedSerialObj?.id || null : null;
        const actualSerialNum =
          (typeof matchedSerialObj === "object"
            ? matchedSerialObj?.serial_number ||
            matchedSerialObj?.number ||
            matchedSerialObj?.serial
            : matchedSerialObj) || serial;

        return {
          product_id: product.id,
          cart_id: cart.id || null,
          quantity: 1,
          original_price: product.sell_price,
          unit_price: product.sell_price,
          line_total: lineTotal,
          discount_amount: 0,
          discount_type: "none",
          discount_value: 0,
          tax_amount: taxAmount,
          tax_class: product.tax_id || null,
          tax_percentage:
            parseFloat(product.tax_rate || storeData?.tax_percentage) || 0,
          notes: "",
          product_data: JSON.stringify(product),
          inventory_batch_id: config.inventory_batch_id || null,
          inventory_serial_id: serialId,
          inventory_serial: matchedSerialObj || null,
          serial_numbers: [actualSerialNum],
          serial_number: actualSerialNum,
          warranty_start_date: warrantyStart,
          warranty_end_date: warrantyEnd,
          warranty_notes: warrantyNotes,
        };
      });
    } else {
      const basePrice = product.sell_price * quantity;
      const { taxAmount, lineTotal } = calculateItemTax(
        basePrice,
        product,
        quantity,
      );

      newItems = [
        {
          product_id: product.id,
          cart_id: cart.id || null,
          quantity: quantity,
          original_price: product.sell_price,
          unit_price: product.sell_price,
          line_total: lineTotal,
          discount_amount: 0,
          discount_type: "none",
          discount_value: 0,
          tax_amount: taxAmount,
          tax_class: product.tax_id || null,
          tax_percentage:
            parseFloat(product.tax_rate || storeData?.tax_percentage) || 0,
          notes: "",
          product_data: JSON.stringify(product),
          inventory_batch_id: config.inventory_batch_id || null,
          serial_numbers: null,
          serial_number: null,
          warranty_start_date: warrantyStart,
          warranty_end_date: warrantyEnd,
          warranty_notes: warrantyNotes,
        },
      ];
    }

    const tempIds = newItems.map((_, i) => `temp-${Date.now()}-${i}`);
    const optimisticItems = newItems.map((item, i) => ({
      ...item,
      id: tempIds[i],
      isSyncing: true,
    }));

    const allProducts = [...currentProducts, ...optimisticItems];
    setCurrentProducts(allProducts);
    setUpdatingItemIds((prev) => [...prev, ...tempIds]);

    try {
      if (!cart.id) {
        const customerId =
          cart.customer_id || customer?.id || initData?.walk_in_customer?.id;
        const newCart = await createCart(customerId, allProducts);
        if (!newCart || !newCart.id) {
          throw new Error("Failed to create cart");
        }
      } else {
        await updateCartTotals(cart.id, allProducts, true);
      }
    } catch (error) {
      setCurrentProducts((prev) =>
        prev.filter((i) => !tempIds.includes(i.id)),
      );
      showToast(
        error.response?.data?.message ||
        error.message ||
        "Failed to add items to cart",
        "error",
      );
    } finally {
      setUpdatingItemIds((prev) => prev.filter((id) => !tempIds.includes(id)));
    }
  };

  const handleInventorySelected = (selection) => {
    if (inventorySelectorProduct) {
      let serialSelection = [];
      if (inventorySelectorType === "serial") {
        const raw = Array.isArray(selection) ? selection : [selection];
        serialSelection = Array.from(
          new Set(raw.map(normalizeSerial).filter(Boolean)),
        );
      }

      const config = {
        inventory_batch_id:
          inventorySelectorType === "standard" ? selection : null,
        serial_numbers: serialSelection,
      };

      finishAddingToCart(
        inventorySelectorProduct,
        inventorySelectorQuantity,
        config,
      );
    }
    setShowInventorySelector(false);
  };

  const updateCartItemQuantity = async (item, newQuantity) => {
    if (currentSaleData || (cart?.id && String(cart.id).startsWith("sale-"))) {
      showToast("Sale items are finalized and cannot be modified.", "warning");
      return;
    }
    if (newQuantity < 1) {
      showToast("Quantity must be at least 1", "error");
      return;
    }

    // Check inventory if manage_inventory is enabled
    const productData =
      typeof item.product_data === "string"
        ? JSON.parse(item.product_data)
        : item.product_data || {};

    const trackingType = productData.manage_inventory
      ? productData.tracking_type || (productData.is_serialized ? "serial" : "none")
      : "none";
    if (trackingType === "serial" && newQuantity > 1) {
      showToast("Serialized items must have a quantity of 1 per serial number", "error");
      return;
    }
    if (productData.manage_inventory) {
      const availableStock =
        parseFloat(productData.sellable_qty || 0) + item.quantity;
      if (newQuantity > availableStock) {
        showToast(`Only ${availableStock} units available in stock`, "error");
        return;
      }
    }

    setUpdatingItemIds((prev) => [...prev, item.id]);

    const unitPrice = parseFloat(item.unit_price);
    const basePrice = unitPrice * newQuantity;
    let itemDiscount = 0;

    if (item.discount_type === "percentage" && item.discount_value > 0) {
      itemDiscount = (basePrice * parseFloat(item.discount_value)) / 100;
    } else if (item.discount_type === "fixed" && item.discount_value > 0) {
      itemDiscount = parseFloat(item.discount_value);
    }

    const discountedPrice = basePrice - itemDiscount;

    const { taxAmount: newTaxAmount, lineTotal } = calculateItemTax(
      discountedPrice,
      productData,
      newQuantity,
    );

    const updatedItem = {
      ...item,
      quantity: newQuantity,
      discount_amount: itemDiscount,
      tax_amount: newTaxAmount,
      line_total: lineTotal,
      custom_fields:
        typeof item.custom_fields === "object"
          ? JSON.stringify(item.custom_fields || "")
          : item.custom_fields || "",
    };

    const previousProducts = [...currentProducts];
    const updatedProducts = currentProducts.map((p) =>
      p.id === item.id ? updatedItem : p,
    );
    setCurrentProducts(updatedProducts);

    try {
      const cartId = item.cart_id || cart.id;
      await updateCartTotals(cartId, updatedProducts);

      setLocalQuantities((prev) => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } catch (error) {
      setCurrentProducts(previousProducts);
      setLocalQuantities((prev) => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      showToast(
        error.response?.data?.message || "Failed to update quantity",
        "error",
      );
    } finally {
      setUpdatingItemIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  // Keep ref in sync for debounced quantity callback
  useEffect(() => {
    updateCartItemQuantityRef.current = updateCartItemQuantity;
  });

  const updateCartTotals = async (cartId, cartItems, immediate = false) => {
    if (currentSaleData || (cartId && String(cartId).startsWith("sale-"))) {
      return;
    }
    const session = activeCartSessionRef.current;
    try {
      const items = Array.isArray(cartItems)
        ? cartItems
        : (cartItems || currentProducts);

      const { isValid, duplicates } = validateCartSerials(items);
      if (!isValid) {
        showToast(
          `Cannot update cart: Duplicate serial number(s) detected: ${duplicates.join(", ")}`,
          "error",
        );
        return;
      }

      const { payload, completeCartState } = formatCartPayload(
        cart,
        items,
        { id: cartId },
      );

      // Update local state immediately for fast UI
      setCart(completeCartState);
      dispatch(setcurrent_cart(completeCartState));

      if (cartSyncTimerRef.current) {
        clearTimeout(cartSyncTimerRef.current);
      }

      const syncToBackend = async () => {
        if (activeCartSessionRef.current !== session) return;
        try {
          setQtyUpdating(true);
          let res;
          if (items.length === 0) {
            res = await apiClient.delete(
              API_ENDPOINTS.SALES.POS_CART_CLEAR_ITEMS(cartId),
            );
          } else {
            res = await apiClient.put(
              API_ENDPOINTS.SALES.POS_CART_DETAILS(cartId),
              payload,
            );
          }

          if (activeCartSessionRef.current !== session) return;

          const serverCart = res.data?.data || res.data;
          if (serverCart && serverCart.id) {
            const finalCart = {
              ...serverCart,
              items: Array.isArray(serverCart.items)
                ? (items.length === 0 ? [] : serverCart.items)
                : (items.length === 0 ? [] : (serverCart.items || [])),
            };
            setCart(finalCart);
            dispatch(setcurrent_cart(finalCart));
            if (Array.isArray(finalCart.items) && finalCart.items.length > 0) {
              setCurrentProducts(finalCart.items);
            } else if (items.length === 0) {
              setCurrentProducts([]);
            }
          }
        } catch (error) {
          if (activeCartSessionRef.current !== session) return;
          console.error("Failed to sync cart totals:", error);
        } finally {
          setQtyUpdating(false);
        }
      };

      if (immediate) {
        await syncToBackend();
      } else {
        cartSyncTimerRef.current = setTimeout(syncToBackend, 800);
      }
    } catch (error) {
      console.error("Failed to update cart totals:", error);
    }
  };

  const calculateItemConfigTotal = () => {
    const basePrice =
      Number(currentItemConfig.unit_price || 0) *
      Number(currentItemConfig.quantity || 1);
    let discountAmount = 0;

    if (
      currentItemConfig.discount_type === "percentage" &&
      currentItemConfig.discount_value > 0
    ) {
      discountAmount =
        (basePrice * Number(currentItemConfig.discount_value)) / 100;
    } else if (
      currentItemConfig.discount_type === "fixed" &&
      currentItemConfig.discount_value > 0
    ) {
      discountAmount = Number(currentItemConfig.discount_value);
    }

    const discountedPrice = basePrice - discountAmount;

    // Calculate tax and total using the new system
    const { taxAmount, lineTotal } = calculateItemTax(
      discountedPrice,
      selectedModalProduct,
      Number(currentItemConfig.quantity || 1),
    );

    return {
      basePrice: Number(basePrice),
      discountAmount: Number(discountAmount),
      discountedPrice: Number(discountedPrice),
      taxAmount: Number(taxAmount),
      lineTotal: Number(lineTotal),
    };
  };

  const handleSaveItemConfig = async () => {
    if (!currentItemConfig.product_id) {
      showToast("Please select a product", "error");
      return;
    }

    if (currentItemConfig.quantity < 1) {
      showToast("Quantity must be at least 1", "error");
      return;
    }

    const trackingType = selectedModalProduct?.manage_inventory
      ? selectedModalProduct?.tracking_type ||
      (selectedModalProduct?.is_serialized ? "serial" : "none")
      : "none";

    // Validate Serials for Serialized Products
    if (trackingType === "serial") {
      const serials = (currentItemConfig.serial_numbers || [])
        .map(normalizeSerial)
        .filter(Boolean);

      if (serials.length !== currentItemConfig.quantity) {
        showToast(
          `Please select exactly ${currentItemConfig.quantity} serial numbers`,
          "error",
        );
        return;
      }

      // Check duplicates within selection
      const seen = new Set();
      for (const s of serials) {
        const lower = s.toLowerCase();
        if (seen.has(lower)) {
          showToast(`Duplicate serial number "${s}" cannot be added`, "error");
          return;
        }
        seen.add(lower);
      }

      // Check against existing items in cart (excluding this item if editing)
      for (const s of serials) {
        if (isSerialInCart(currentProducts, s, editingCartItem?.id)) {
          showToast(`Serial number "${s}" is already in the cart`, "error");
          return;
        }
      }
    }

    // Validate Batch for Standard Tracking
    if (trackingType === "standard" && !currentItemConfig.inventory_batch_id) {
      showToast("Please select a batch for this product", "error");
      return;
    }

    // Check inventory if manage_inventory is enabled
    if (selectedModalProduct && selectedModalProduct.manage_inventory) {
      const availableStock = parseFloat(selectedModalProduct.sellable_qty || 0);

      // If editing, subtract the current cart item quantity from available stock
      let availableForThisItem = availableStock;
      if (editingCartItem) {
        availableForThisItem += editingCartItem.quantity;
      }

      if (currentItemConfig.quantity > availableForThisItem) {
        showToast(
          `Only ${availableForThisItem} units available in stock`,
          "error",
        );
        return;
      }
    }

    // sonnerToast.info(editingCartItem ? "Updating item..." : "Adding item...");

    // Calculate final values
    const { discountAmount, lineTotal, taxAmount } = calculateItemConfigTotal();
    const serialNumbers = currentItemConfig.serial_numbers || [];

    let updatedProducts = [];
    let affectedItemIds = [];

    // Special handling for adding NEW items with serial numbers (split them)
    if (!editingCartItem && serialNumbers.length > 1) {
      const tempIds = serialNumbers.map((_, i) => `temp-${Date.now()}-${i}`);
      affectedItemIds = tempIds;
      const serialItems = serialNumbers.map((serial, idx) => {
        const basePrice = Number(currentItemConfig.unit_price || 0);
        let discAmt = 0;
        if (currentItemConfig.discount_type === "percentage") {
          discAmt =
            (basePrice * Number(currentItemConfig.discount_value || 0)) / 100;
        } else if (currentItemConfig.discount_type === "fixed") {
          // Spread fixed discount across all units
          discAmt =
            Number(currentItemConfig.discount_value || 0) /
            serialNumbers.length;
        }

        const discountedPrice = basePrice - discAmt;
        const { taxAmount: taxAm, lineTotal: lTotal } = calculateItemTax(
          discountedPrice,
          selectedModalProduct,
          1,
        );

        const matchedSerial = Array.isArray(selectedModalProduct?.serial_numbers)
          ? selectedModalProduct.serial_numbers.find(
            (s) => s.serial_number === serial || s.id === serial,
          )
          : null;
        const actualSerial = matchedSerial?.serial_number || serial;
        const serialId = matchedSerial?.id || null;

        return {
          ...currentItemConfig,
          id: tempIds[idx],
          isSyncing: true,
          quantity: 1,
          discount_amount: discAmt,
          tax_amount: taxAm,
          line_total: lTotal,
          inventory_serial_id: serialId,
          inventory_serial: matchedSerial || null,
          serial_numbers: [actualSerial],
          serial_number: actualSerial,
          warranty_start_date: currentItemConfig.warranty_start_date || null,
          warranty_end_date: currentItemConfig.warranty_end_date || null,
          warranty_notes: currentItemConfig.warranty_notes || null,
        };
      });
      updatedProducts = [...currentProducts, ...serialItems];
    } else if (editingCartItem) {
      affectedItemIds = [editingCartItem.id];
      // Normal handling for editing
      const firstSerial = serialNumbers[0] || currentItemConfig.serial_number || null;
      const matchedSerial = firstSerial && Array.isArray(selectedModalProduct?.serial_numbers)
        ? selectedModalProduct.serial_numbers.find(
          (s) => s.serial_number === firstSerial || s.id === firstSerial,
        )
        : null;
      const actualSerial = matchedSerial?.serial_number || firstSerial;

      const itemData = {
        ...currentItemConfig,
        id: editingCartItem.id,
        isSyncing: true,
        inventory_serial_id:
          matchedSerial?.id || currentItemConfig.inventory_serial_id || null,
        inventory_serial: matchedSerial || currentItemConfig.inventory_serial || null,
        serial_numbers: actualSerial ? [actualSerial] : null,
        serial_number: actualSerial,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        line_total: lineTotal,
        warranty_start_date: currentItemConfig.warranty_start_date || null,
        warranty_end_date: currentItemConfig.warranty_end_date || null,
        warranty_notes: currentItemConfig.warranty_notes || null,
      };
      updatedProducts = currentProducts.map((p) =>
        p.id === editingCartItem.id ? itemData : p,
      );
    } else {
      // Normal handling for single new item
      const tempId = `temp-${Date.now()}-0`;
      affectedItemIds = [tempId];
      const firstSerial = serialNumbers[0] || currentItemConfig.serial_number || null;
      const matchedSerial = firstSerial && Array.isArray(selectedModalProduct?.serial_numbers)
        ? selectedModalProduct.serial_numbers.find(
          (s) => s.serial_number === firstSerial || s.id === firstSerial,
        )
        : null;
      const actualSerial = matchedSerial?.serial_number || firstSerial;

      const itemData = {
        ...currentItemConfig,
        id: tempId,
        isSyncing: true,
        inventory_serial_id:
          matchedSerial?.id || currentItemConfig.inventory_serial_id || null,
        inventory_serial: matchedSerial || null,
        serial_numbers: actualSerial ? [actualSerial] : null,
        serial_number: actualSerial,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        line_total: lineTotal,
        warranty_start_date: currentItemConfig.warranty_start_date || null,
        warranty_end_date: currentItemConfig.warranty_end_date || null,
        warranty_notes: currentItemConfig.warranty_notes || null,
      };
      updatedProducts = [...currentProducts, itemData];
    }

    setCurrentProducts(updatedProducts);
    if (affectedItemIds.length > 0) {
      setUpdatingItemIds((prev) => [...prev, ...affectedItemIds]);
    }

    try {
      if (!cart.id) {
        const customerId =
          customer?.id || initData?.walk_in_customer?.id || cart.customer_id;
        await createCart(customerId, updatedProducts);
      } else {
        await updateCartTotals(cart.id, updatedProducts, true);
      }

      // Close modal and reset
      setShowItemModal(false);
      setEditingCartItem(null);
      setSelectedModalProduct(null);
      setCurrentItemConfig({
        product_id: null,
        cart_id: null,
        quantity: 1,
        original_price: 0,
        unit_price: 0,
        line_total: 0,
        discount_amount: 0,
        discount_type: "none",
        discount_value: 0,
        tax_amount: 0,
        tax_id: null,
        notes: "",
        product_data: null,
        serial_numbers: [],
        inventory_batch_id: null,
        warranty_start_date: null,
        warranty_end_date: null,
        warranty_notes: null,
      });

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to save item",
        "error",
      );
    } finally {
      if (affectedItemIds.length > 0) {
        setUpdatingItemIds((prev) =>
          prev.filter((id) => !affectedItemIds.includes(id)),
        );
      }
    }
  };

  const handleEditCartItem = (item) => {
    if (currentSaleData) {
      showToast("Finalized sales cannot be edited.", "warning");
      return;
    }
    if (!hasPermission("cart item update")) {
      showToast("You don't have permission to edit cart items", "error");
      return;
    }
    if (
      !item?.id ||
      String(item.id).startsWith("temp-") ||
      item.isSyncing ||
      updatingItemIds.includes(item.id)
    ) {
      showToast("Please wait for the item to finish syncing", "info");
      return;
    }
    try {
      const productData = JSON.parse(item.product_data);
      const product =
        products.find((p) => p.id === productData.id) || productData;

      const trackingType = product.manage_inventory
        ? product.tracking_type || (product.is_serialized ? "serial" : "none")
        : "none";

      let itemSerials = [];
      if (item.serial_numbers) {
        try {
          itemSerials =
            typeof item.serial_numbers === "string"
              ? JSON.parse(item.serial_numbers)
              : item.serial_numbers;
        } catch {
          itemSerials = [];
        }
      }

      // Fallback to inventory_serial if serial_numbers is empty
      if (
        (!itemSerials || itemSerials.length === 0) &&
        item.inventory_serial?.serial_number
      ) {
        itemSerials = [item.inventory_serial.serial_number];
      }

      setSelectedModalProduct(product);
      setCurrentItemConfig({
        product_id: item.product_id,
        cart_id: item.cart_id,
        quantity: item.quantity,
        original_price: item.original_price,
        unit_price: item.unit_price,
        line_total: item.line_total,
        discount_amount: item.discount_amount || 0,
        discount_type: item.discount_type || "none",
        discount_value: item.discount_value || 0,
        tax_amount: item.tax_amount || 0,
        tax_id: item.tax_id || null,
        notes: item.notes || "",
        product_data: item.product_data,
        serial_numbers: itemSerials,
        inventory_batch_id: item.inventory_batch_id || null,
        warranty_start_date: item.warranty_start_date || null,
        warranty_end_date: item.warranty_end_date || null,
        warranty_notes: item.warranty_notes || null,
      });
      setEditingCartItem(item);

      if (trackingType === "serial") {
        fetchAvailableSerials(product);
      } else if (trackingType === "standard") {
        fetchAvailableBatches(product);
      }

      setShowItemModal(true);
    } catch (error) {
      console.error("Failed to parse product data", error);
      showToast("Failed to load item data", "error");
    }
  };

  const handleQuantityChange = useCallback((item, newValue) => {
    if (!hasPermission("cart item update")) {
      showToast("You don't have permission to update cart items", "error");
      return;
    }
    // Update local state immediately for real-time display
    setLocalQuantities((prev) => ({
      ...prev,
      [item.id]: newValue,
    }));

    const quantity = parseInt(newValue);

    if (isNaN(quantity) || quantity < 1) {
      return;
    }

    // Clear existing timer for this item
    if (quantityUpdateTimers.current[item.id]) {
      clearTimeout(quantityUpdateTimers.current[item.id]);
    }

    // Set new timer with 0.5 second debounce
    quantityUpdateTimers.current[item.id] = setTimeout(() => {
      updateCartItemQuantityRef.current(item, quantity);
      delete quantityUpdateTimers.current[item.id];
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchCustomers = async (searchTerm) => {
    setCustomerLoading(true);
    try {
      const res = await apiClient.get(API_ENDPOINTS.SALES.POS_CUSTOMERS, {
        params: {
          q: searchTerm,
        },
      });
      if (res.status === 200 || res.status === 201) {
        setCustomers(res.data.data);
      }
    } catch (error) {
      console.log(error.response?.data?.message);
    } finally {
      setCustomerLoading(false);
    }
    return searchTerm;
  };

  const fetchCustomer = async (cus_id) => {
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.SALES.CUSTOMER_DETAILS(cus_id),
      );
      if (res.status === 200 || res.status === 201) {
        setCustomer(res.data.data);
      }
    } catch (error) {
      console.log(error.response?.data?.message);
    }
  };

  const fetchBrands = async () => {
    if (!hasPermission("brand read")) return;
    try {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS);
      if (res.status === 200 || res.status === 201) {
        setBrands(res.data.data);
      }
    } catch (error) {
      console.log(error.response?.data?.message);
    }
  };

  const fetchProducts = async (
    cat_id,
    brand_id,
    searchTxt,
    updateState = true,
    page = 1,
    sellable = true,
    storeId = currentStore,
  ) => {
    if (updateState) {
      if (page === 1) {
        setProductsLoading(true);
      } else {
        setIsFetchingMore(true);
      }
    }
    try {
      const params = {
        category: cat_id || undefined,
        brand: brand_id || undefined,
        search: searchTxt || undefined,
        perPage: 10,
        page: page,
        store_id: storeId,
        sellable: sellable,
        with: [
          "brand",
          "model",
          "unit",
          "categories",
          "batches",
          "serial_numbers",
        ],
      };

      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: params,
      });

      if (res.status === 200 || res.status === 201) {
        const finalProducts = res.data.data || [];
        const meta = res.data.meta;
        const lastPage = meta?.last_page || 1;
        setHasMoreProducts(page < lastPage);
        setProductPage(page);

        if (updateState) {
          if (page === 1) {
            setProducts(finalProducts);
          } else {
            setProducts((prev) => [...prev, ...finalProducts]);
          }
          setProductsLoading(false);
          setIsFetchingMore(false);
          setProductsError(false);
        }
        return finalProducts;
      }
      return [];
    } catch (error) {
      if (updateState) {
        setProductsLoading(false);
        setIsFetchingMore(false);
        setProductsError(true);
      }
      console.log(error);
      return [];
    }
  };

  const handleLoadMoreProducts = () => {
    if (hasMoreProducts && !productsLoading) {
      const nextPage = productPage + 1;
      fetchProducts(
        currentCat,
        selectedBrand,
        activeTab === "all-products" ? allProductsSearch : "",
        true,
        nextPage,
      );
    }
  };

  const searchDropdownProducts = useCallback(
    async (searchTerm) => {
      if (!searchTerm || !searchTerm.trim()) {
        return [];
      }
      try {
        const params = {
          search: searchTerm.trim(),
          perPage: 25,
          page: 1,
          store_id: currentStore,
          sellable: true,
          with: [
            "brand",
            "model",
            "unit",
            "categories",
            "batches",
            "serial_numbers",
          ],
        };
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
          params,
        });
        if (res.status === 200 || res.status === 201) {
          const data = res.data?.data || res.data || [];
          return Array.isArray(data) ? data : [];
        }
        return [];
      } catch (err) {
        console.error("Dropdown search error:", err);
        return [];
      }
    },
    [currentStore],
  );

  const handleAllProductsSearchChange = (value) => {
    setAllProductsSearch(value);

    if (allProductsSearchTimeoutRef.current) {
      clearTimeout(allProductsSearchTimeoutRef.current);
    }

    const trimmed = typeof value === "string" ? value.trim() : "";
    if (trimmed.length > 0) {
      setProductsLoading(true);
    }

    allProductsSearchTimeoutRef.current = setTimeout(async () => {
      fetchProducts(null, selectedBrand, trimmed);
    }, 300);
  };

  const handleCategorySearchChange = (val) => {
    setCategorySearch(val);

    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }

    const trimmed = typeof val === "string" ? val.trim() : "";
    if (!trimmed) {
      setDebouncedCategorySearch("");
      return;
    }

    categorySearchTimeoutRef.current = setTimeout(() => {
      setDebouncedCategorySearch(trimmed);
    }, 300);
  };

  const handleBrandChange = (option) => {
    const brandValue = option ? option.value : null;
    setSelectedBrand(brandValue);

    if (activeTab === "all-products") {
      fetchProducts(null, brandValue, allProductsSearch);
    } else {
      fetchProducts(currentCat, brandValue, "");
    }
  };

  // Filter products by price on the frontend
  const filterProductsByPrice = (productsList) => {
    if (!priceFilter.min && !priceFilter.max) {
      return productsList;
    }

    return productsList.filter((product) => {
      const price = parseFloat(product.price || 0);
      const min = priceFilter.min ? parseFloat(priceFilter.min) : 0;
      const max = priceFilter.max ? parseFloat(priceFilter.max) : Infinity;

      return price >= min && price <= max;
    });
  };

  const applyFilters = () => {
    setFilterModalOpen(false);
    // Fetch products will be called, and price filter will be applied in the render
    if (activeTab === "all-products") {
      fetchProducts(null, selectedBrand, allProductsSearch);
    } else if (currentCat) {
      fetchProducts(currentCat, selectedBrand, "");
    }
  };

  const clearFilters = () => {
    setSelectedBrand(null);
    setPriceFilter({ min: "", max: "" });
    if (activeTab === "all-products") {
      fetchProducts(null, null, allProductsSearch);
    } else if (currentCat) {
      fetchProducts(currentCat, null, "");
    }
  };

  const handleCategorySelect = (categoryId, categoryName = "") => {
    setCurrentCat(categoryId);
    setCategorySearch("");
    setDebouncedCategorySearch("");
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }
    if (categoryName) {
      setCurrentCatName(categoryName);
    } else if (categoryId) {
      const found = categories.find((c) => String(c.id) === String(categoryId));
      if (found) setCurrentCatName(found.name);
    } else {
      setCurrentCatName("");
    }
    fetchProducts(categoryId, selectedBrand, "");
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);

    if (tabName === "all-products") {
      fetchProducts(null, selectedBrand, allProductsSearch);
    } else {
      setCurrentCat(null);
      setCurrentCatName("");
      setCategorySearch("");
      setDebouncedCategorySearch("");
      if (categorySearchTimeoutRef.current) {
        clearTimeout(categorySearchTimeoutRef.current);
      }
      setProducts([]);
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (init) {
      if (activeTab === "all-products") {
        fetchProducts(null, selectedBrand, allProductsSearch);
      } else if (currentCat) {
        fetchProducts(currentCat, selectedBrand, "");
      } else {
        fetchProducts(null, selectedBrand, "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init]);

  const prevStoreRef = useRef(currentStore);

  useEffect(() => {
    if (prevStoreRef.current && prevStoreRef.current !== currentStore) {
      // Store changed! Reset POS and refetch products
      setCurrentProducts([]);
      setCart({
        id: null,
        customer_id: null,
        store_id: currentStore,
        user_id: currentUser?.id || null,
        subtotal: 0,
        tax_amount: 0,
        discount_type: "none",
        discount_amount: 0,
        discount_value: 0,
        item_count: 0,
        total: 0,
        status: "active",
        notes: null,
        custom_fields: null,
        items: [],
      });

      if (initData?.walk_in_customer) {
        setCustomer(initData.walk_in_customer);
      }

      dispatch(setcurrent_cart({}));
      setInProgress(false);
      lastLoadedCartRef.current = null;
      setCurrentSaleData(null);
      setPaymentData({
        sale_id: "",
        amount: 0,
        payment_method: availablePaymentMethods?.length > 0 ? (availablePaymentMethods[0].id || availablePaymentMethods[0]) : "cash",
        transaction_id: "",
        payment_date: getCurrentDateInStoreTimezone(),
        authorization_code: "",
        notes: "",
        payment_status: "completed",
      });
      setPayments([]);
      setPaidAmount(0);

      // Refetch products with new store
      if (activeTab === "all-products") {
        fetchProducts(
          null,
          selectedBrand,
          allProductsSearch,
          true,
          1,
          true,
          currentStore,
        );
      } else if (currentCat) {
        fetchProducts(
          currentCat,
          selectedBrand,
          "",
          true,
          1,
          true,
          currentStore,
        );
      } else {
        fetchProducts(
          null,
          selectedBrand,
          "",
          true,
          1,
          true,
          currentStore,
        );
      }
    }
    prevStoreRef.current = currentStore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  useEffect(() => {
    if (cart.customer_id) {
      fetchCustomer(cart.customer_id);
    } else if (initData?.walk_in_customer) {
      setCustomer(initData.walk_in_customer);
    }
  }, [cart.customer_id, initData]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = quantityUpdateTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, []);

  const clearAllItems = useCallback(() => {
    if (currentSaleData) {
      prepareNewSale();
      showToast("Exited sale view. Started a new cart.", "info");
      return;
    }
    if (!hasPermission("cart delete")) {
      showToast("You don't have permission to clear the cart", "error");
      return;
    }
    if (!currentProducts || currentProducts.length === 0) {
      return;
    }
    setIsClearCartDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, currentSaleData, currentProducts]);

  const confirmClearAllItems = async () => {
    setIsClearCartDialogOpen(false);
    if (currentSaleData) {
      prepareNewSale();
      return;
    }
    handleProtectedAction(async () => {
      const cartId =
        cart.id ||
        (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

      // Invalidate any in-flight backend requests from previous cart actions
      activeCartSessionRef.current += 1;

      // Cancel any pending debounced timers
      if (cartSyncTimerRef.current) {
        clearTimeout(cartSyncTimerRef.current);
        cartSyncTimerRef.current = null;
      }
      if (quantityUpdateTimers.current) {
        Object.values(quantityUpdateTimers.current).forEach((timer) => {
          clearTimeout(timer);
        });
        quantityUpdateTimers.current = {};
      }

      const emptyCartState = {
        ...cart,
        id: cartId || null,
        items: [],
        subtotal: 0,
        item_count: 0,
        tax_amount: 0,
        total: 0,
        discount_value: 0,
        discount_amount: 0,
      };

      // Optimistically clear local frontend state immediately
      setCurrentProducts([]);
      setLocalQuantities({});
      setCart(emptyCartState);
      dispatch(setcurrent_cart(emptyCartState));

      if (!cartId) {
        showToast("Cart cleared successfully", "success");
        return;
      }

      try {
        const res = await apiClient.delete(
          API_ENDPOINTS.SALES.POS_CART_CLEAR_ITEMS(cartId),
        );
        const serverCart = res.data?.data || res.data;
        if (serverCart && serverCart.id && typeof serverCart === "object") {
          const finalCart = {
            ...serverCart,
            items: [],
          };
          setCart(finalCart);
          dispatch(setcurrent_cart(finalCart));
        }
        showToast("Cart cleared successfully", "success");
      } catch (err) {
        console.error("Failed to clear cart:", err);
        showToast("Failed to clear cart", "error");
        fetchCartItems(cartId);
      }
    }, "Clear Cart");
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // F1 - Focus search
      if (e.key === "F1") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // F2 - Clear cart
      if (e.key === "F2") {
        e.preventDefault();
        clearAllItems();
      }
      // F3 - Customer search
      if (e.key === "F3") {
        e.preventDefault();
        setCustomerSearchExpanded(true);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [clearAllItems]);

  const removeCartItem = async (itemId) => {
    if (currentSaleData) {
      showToast("Finalized sales cannot be edited.", "warning");
      return;
    }
    if (!hasPermission("cart item delete")) {
      showToast("You don't have permission to remove items from cart", "error");
      return;
    }
    handleProtectedAction(() => performRemoveCartItem(itemId), "Remove Item");
  };

  const performRemoveCartItem = async (itemId) => {
    if (
      !itemId ||
      String(itemId).startsWith("temp-") ||
      updatingItemIds.includes(itemId)
    ) {
      showToast("Please wait for the item to finish syncing", "info");
      return;
    }

    const item = currentProducts.find((p) => p.id === itemId);
    const cartId =
      cart.id ||
      item?.cart_id ||
      (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

    if (!cartId) {
      setCurrentProducts((prev) => prev.filter((i) => i.id !== itemId));
      return;
    }

    setUpdatingItemIds((prev) => [...prev, itemId]);

    // Optimistically remove from state
    const previousProducts = [...currentProducts];
    const updatedProducts = currentProducts.filter((i) => i.id !== itemId);
    setCurrentProducts(updatedProducts);

    try {
      if (updatedProducts.length > 0) {
        await updateCartTotals(cartId, updatedProducts, true);
      } else {
        const res = await apiClient.delete(
          API_ENDPOINTS.SALES.POS_CART_CLEAR_ITEMS(cartId),
        );
        const serverCart = res.data?.data || res.data;
        const emptyCartState = {
          ...cart,
          id: cartId,
          ...(serverCart && typeof serverCart === "object" ? serverCart : {}),
          items: [],
          subtotal: 0,
          item_count: 0,
          tax_amount: 0,
          total: 0,
          discount_value: 0,
          discount_amount: 0,
        };
        setCart(emptyCartState);
        dispatch(setcurrent_cart(emptyCartState));
      }

      // Clear local quantity for removed item
      setLocalQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } catch (error) {
      // Rollback on error
      setCurrentProducts(previousProducts);
      showToast(
        error.response?.data?.message || "Failed to remove item from cart",
        "error",
      );
    } finally {
      setUpdatingItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const calculateSubTotal = () => {
    if (!currentProducts || currentProducts.length < 1) return 0;

    let subtotal = 0;
    currentProducts.forEach((product) => {
      const lineTotal = parseFloat(product.line_total) || 0;
      // lineTotal is already pre-tax (exclusive) or full price (inclusive with taxAmount=0)
      subtotal += lineTotal;
    });

    return round2(subtotal);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubTotal();
    const discountValue = calculateCartDiscount();

    let totalTax = 0;
    currentProducts.forEach((product) => {
      totalTax += parseFloat(product.tax_amount) || 0;
    });

    return round2(subtotal - discountValue + totalTax);
  };

  const calculateCartDiscount = () => {
    const subtotal = calculateSubTotal();
    if (subtotal <= 0) return 0;

    let discountValue = 0;
    if (cart.discount_type === "percentage" && cart.discount_value > 0) {
      discountValue = (subtotal * parseFloat(cart.discount_value)) / 100;
    } else if (cart.discount_type === "fixed" && cart.discount_value > 0) {
      discountValue = parseFloat(cart.discount_value);
    }

    // Cap at subtotal - warning is shown by the effect below
    return Math.min(discountValue, subtotal);
  };

  // Warn the user (as a proper side effect) when the cart discount exceeds the
  // subtotal. Kept separate from the pure calculateCartDiscount function so that
  // no toast is ever fired during the render phase.
  useEffect(() => {
    const subtotal = calculateSubTotal();
    if (subtotal <= 0) return;

    let rawDiscount = 0;
    if (cart.discount_type === "percentage" && cart.discount_value > 0) {
      rawDiscount = (subtotal * parseFloat(cart.discount_value)) / 100;
    } else if (cart.discount_type === "fixed" && cart.discount_value > 0) {
      rawDiscount = parseFloat(cart.discount_value);
    }

    if (rawDiscount > subtotal) {
      showToast(
        `Discount limited to ${currencySymbol}${subtotal.toFixed(2)} to maintain cart total`,
        "warning",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.discount_type, cart.discount_value, currentProducts]);

  const handleCheckout = () => {
    // If sale already loaded (pending balance or completed), open modal to process payment or view invoice
    if (currentSaleData) {
      setPaymentModalOpen(true);
      return;
    }

    // Validate cart has items
    if (!currentProducts || currentProducts.length === 0) {
      showToast(
        "Please add products to cart before proceeding to checkout",
        "error",
      );
      return;
    }

    // Validate no duplicate serials before proceeding to checkout
    const { isValid: areSerialsValid, duplicates: checkoutDuplicates } =
      validateCartSerials(currentProducts);
    if (!areSerialsValid) {
      showToast(
        `Cannot proceed to checkout: Duplicate serial number(s) found in cart: ${checkoutDuplicates.join(", ")}. Please remove duplicate items first.`,
        "error",
      );
      return;
    }

    const cartId =
      cart.id ||
      (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

    if (!cartId) {
      showToast("No active cart found. Please add items again.", "error");
      return;
    }

    const totalToPay = calculateTotal();
    const defaultMethod =
      availablePaymentMethods?.length > 0
        ? (availablePaymentMethods[0].id || availablePaymentMethods[0])
        : "cash";

    // Initialize payment data without completing cart or calling any APIs
    setPaymentData({
      sale_id: "",
      amount: parseFloat(totalToPay.toFixed(2)),
      payment_method: defaultMethod,
      transaction_id: `TXN-${Date.now()}`,
      payment_date: getCurrentDateInStoreTimezone(),
      notes: "",
      payment_status: "completed",
    });

    setPayments([]);
    setPaidAmount(0);
    setCurrentSaleData(null);

    // Just show the payment modal, no API requests!
    setPaymentModalOpen(true);
  };

  const processPayment = async () => {
    setPaymentProcessing(true);
    try {
      if (!paymentData.payment_method || !paymentData.amount || paymentData.amount <= 0) {
        showToast(
          "Please select a payment method and enter a valid amount",
          "error",
        );
        setPaymentProcessing(false);
        return;
      }

      // Validate no duplicate serials before completing cart or payment
      const { isValid: areSerialsValid, duplicates: paymentDuplicates } =
        validateCartSerials(currentProducts);
      if (!areSerialsValid) {
        showToast(
          `Cannot complete order: Duplicate serial number(s) found in cart: ${paymentDuplicates.join(", ")}. Please remove duplicate items first.`,
          "error",
        );
        setPaymentProcessing(false);
        return;
      }

      let activeSale = currentSaleData;

      // On first payment, complete the cart first to create the sale in backend
      if (!activeSale?.id) {
        const cartId =
          cart.id ||
          (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

        if (!cartId) {
          showToast("No active cart found. Please add items again.", "error");
          setPaymentProcessing(false);
          return;
        }

        try {
          await updateCart({
            ...cart,
            id: cartId,
            status: "active",
            subtotal: calculateSubTotal(),
            total: calculateTotal(),
          });
        } catch (updateErr) {
          console.warn("Cart update before complete warning:", updateErr);
        }

        const saleResponse = await apiClient.post(
          API_ENDPOINTS.SALES.POS_CART_COMPLETE(cartId),
          null,
        );

        if (saleResponse.status === 200 || saleResponse.status === 201) {
          activeSale = saleResponse.data.data;
          setCurrentSaleData(activeSale);
        } else {
          throw new Error("Failed to complete cart into sale");
        }
      }

      // Calculate payment amount
      const saleTotal = parseFloat(activeSale.total || calculateTotal());
      const balanceDue = saleTotal - paidAmount;

      const isCashPayment =
        paymentData.payment_method === "cash" ||
        availablePaymentMethods.find(
          (m) => (m.id || m.slug || m) === paymentData.payment_method,
        )?.code === "cash" ||
        availablePaymentMethods
          .find((m) => (m.id || m.slug || m) === paymentData.payment_method)
          ?.name?.toLowerCase()
          .includes("cash");

      const actualAmount =
        isCashPayment && paymentData.amount > balanceDue + 0.001
          ? parseFloat(balanceDue.toFixed(2))
          : parseFloat(Number(paymentData.amount).toFixed(2));

      const paymentPayload = {
        ...paymentData,
        sale_id: activeSale.id,
        amount: actualAmount,
      };

      const response = await apiClient.post(
        API_ENDPOINTS.SALES.POS_SALE_PAYMENT(activeSale.id),
        paymentPayload,
      );

      if (response.status === 200 || response.status === 201) {
        const newPaidAmount = parseFloat((paidAmount + actualAmount).toFixed(2));
        setPaidAmount(newPaidAmount);
        setPayments((prev) => [...prev, { ...paymentPayload, amount: actualAmount }]);

        const isFullyCovered = newPaidAmount >= saleTotal - 0.001;

        if (isFullyCovered) {
          showToast("Payment recorded - sale is fully paid!", "success");

          // Clear cart in Redux since sale is paid
          dispatch(setcurrent_cart({}));
          setInProgress(false);

          // Generate PDF invoice in background
          handleGeneratePDFReport(activeSale.id);
        } else {
          // Prepare next split payment
          const nextRemaining = parseFloat(Math.max(0, saleTotal - newPaidAmount).toFixed(2));
          setPaymentData({
            ...paymentData,
            sale_id: activeSale.id,
            amount: nextRemaining,
            transaction_id: `TXN-${Date.now()}`,
            notes: "",
            payment_status: "completed",
          });
          showToast(
            `Added payment of ${currencySymbol}${actualAmount.toFixed(2)}. Remaining: ${currencySymbol}${nextRemaining.toFixed(2)}`,
            "success",
          );
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      showToast(
        error.response?.data?.message || error.message || "Failed to process payment",
        "error",
      );
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCompleteSaleWithoutPayment = async () => {
    setCompleteSaleLoading(true);
    try {
      let activeSale = currentSaleData;
      if (!activeSale?.id) {
        const cartId =
          cart.id ||
          (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

        if (!cartId) {
          showToast("No active cart found to complete sale.", "error");
          setCompleteSaleLoading(false);
          return;
        }

        try {
          await updateCart({
            ...cart,
            id: cartId,
            status: "active",
            subtotal: calculateSubTotal(),
            total: calculateTotal(),
          });
        } catch (updateErr) {
          console.warn("Cart update before complete warning:", updateErr);
        }

        const saleResponse = await apiClient.post(
          API_ENDPOINTS.SALES.POS_CART_COMPLETE(cartId),
          null,
        );

        if (saleResponse.status === 200 || saleResponse.status === 201) {
          activeSale = saleResponse.data.data;
          setCurrentSaleData(activeSale);

          // Update paymentData to link to activeSale.id and sale total
          const saleTotal = parseFloat(activeSale.total || calculateTotal());
          setPaymentData((prev) => ({
            ...prev,
            sale_id: activeSale.id,
            amount: parseFloat(saleTotal.toFixed(2)),
          }));

          // Clear cart in Redux since cart has been converted to sale
          dispatch(setcurrent_cart({}));
          setInProgress(false);

          showToast("Sale completed successfully without payment.", "success");
        } else {
          throw new Error("Failed to complete cart into sale");
        }
      }
    } catch (error) {
      console.error("Complete sale error:", error);
      showToast(
        error.response?.data?.message ||
        error.message ||
        "Failed to complete sale",
        "error",
      );
    } finally {
      setCompleteSaleLoading(false);
    }
  };

  const handlePayLater = async () => {
    setPayLaterLoading(true);
    try {
      let activeSale = currentSaleData;
      if (!activeSale?.id) {
        const cartId =
          cart.id ||
          (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

        if (!cartId) {
          showToast("No active cart found.", "error");
          setPayLaterLoading(false);
          return;
        }

        await updateCart({
          ...cart,
          id: cartId,
          status: "active",
          subtotal: calculateSubTotal(),
          total: calculateTotal(),
        });

        const saleResponse = await apiClient.post(
          API_ENDPOINTS.SALES.POS_CART_COMPLETE(cartId),
          null,
        );

        if (saleResponse.status === 200 || saleResponse.status === 201) {
          activeSale = saleResponse.data.data;
          setCurrentSaleData(activeSale);
        } else {
          throw new Error("Failed to complete cart");
        }
      }

      showToast("Order placed as Pay Later. Ready for new sale.", "success");
      prepareNewSale();
    } catch (error) {
      console.error("Pay later error:", error);
      showToast(
        error.response?.data?.message || "Failed to process Pay Later",
        "error",
      );
    } finally {
      setPayLaterLoading(false);
    }
  };

  const handlePrintInvoice = async () => {
    let activeSale = currentSaleData;
    setInvoiceLoading(true);
    try {
      if (!activeSale?.id) {
        const cartId =
          cart.id ||
          (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

        if (!cartId) {
          showToast("No active cart found to generate invoice.", "error");
          setInvoiceLoading(false);
          return;
        }

        await updateCart({
          ...cart,
          id: cartId,
          status: "active",
          subtotal: calculateSubTotal(),
          total: calculateTotal(),
        });

        const saleResponse = await apiClient.post(
          API_ENDPOINTS.SALES.POS_CART_COMPLETE(cartId),
          null,
        );

        if (saleResponse.status === 200 || saleResponse.status === 201) {
          activeSale = saleResponse.data.data;
          setCurrentSaleData(activeSale);
        } else {
          throw new Error("Failed to complete cart for invoice");
        }
      }

      setPdfModalOpen(true);
      if (!pdfBlobUrl) {
        handleGeneratePDFReport(activeSale.id);
      }
    } catch (error) {
      console.error("Invoice generation error:", error);
      showToast(
        error.response?.data?.message || "Failed to generate invoice",
        "error",
      );
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handlePrintReceipt = async () => {
    let activeSale = currentSaleData;
    setReceiptGenerating(true);
    try {
      if (!activeSale?.id) {
        const cartId =
          cart.id ||
          (currentProducts.length > 0 ? currentProducts[0].cart_id : null);

        if (!cartId) {
          showToast("No active cart found to generate receipt.", "error");
          setReceiptGenerating(false);
          return;
        }

        await updateCart({
          ...cart,
          id: cartId,
          status: "active",
          subtotal: calculateSubTotal(),
          total: calculateTotal(),
        });

        const saleResponse = await apiClient.post(
          API_ENDPOINTS.SALES.POS_CART_COMPLETE(cartId),
          null,
        );

        if (saleResponse.status === 200 || saleResponse.status === 201) {
          activeSale = saleResponse.data.data;
          setCurrentSaleData(activeSale);
        } else {
          throw new Error("Failed to complete cart for receipt");
        }
      }

      await handleGenerateReceiptReport(activeSale.id);
    } catch (error) {
      console.error("Receipt generation error:", error);
      showToast(
        error.response?.data?.message || "Failed to generate thermal receipt",
        "error",
      );
    } finally {
      setReceiptGenerating(false);
    }
  };

  const handleGeneratePDFReport = async (id) => {
    try {
      setPdfGenerating(true);
      setPdfError(null);

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_INVOICE(id),
        { responseType: "blob" },
      );

      if (response.status === 200) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });

        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);

        setPdfGenerated(true);
        setPdfGenerating(false);
      }
    } catch (error) {
      setPdfError(true);
      console.error("PDF Report error:", error);

      setPdfGenerating(false);
      setPdfGenerated(false);
      setPdfBlobUrl(null);

      showToast(
        error.response?.data?.message || "Failed to generate PDF report",
        "error",
      );
    }
  };

  const handleGenerateReceiptReport = async (saleOrId) => {
    const saleId = typeof saleOrId === "object" ? saleOrId?.id : saleOrId;
    if (!saleId) return;
    try {
      setReceiptGenerating(true);
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_RECEIPT(saleId),
        { responseType: "blob" },
      );

      if (response.status === 200) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(blobUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        } else {
          showToast("Please allow popups to print receipt", "warning");
        }
      }
    } catch (error) {
      console.error("Receipt Report error:", error);
      showToast(
        error.response?.data?.message || "Failed to generate thermal receipt",
        "error",
      );
    } finally {
      setReceiptGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (pdfBlobUrl) {
      const invoiceNum = currentSaleData?.invoice_number || cart?.cart_number || "sale";
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `invoice-${invoiceNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("Invoice downloaded successfully!", "success");
    }
  };

  const printPDF = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (!printWindow) {
        showToast("Please allow popups to print the invoice", "warning");
        return;
      }
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const handleClosePDFModal = () => {
    setPdfModalOpen(false);
  };

  const prepareNewSale = () => {
    activeCartSessionRef.current += 1;
    updateInProgress.current = false;

    if (cartSyncTimerRef.current) {
      clearTimeout(cartSyncTimerRef.current);
      cartSyncTimerRef.current = null;
    }
    if (quantityUpdateTimers.current) {
      Object.values(quantityUpdateTimers.current).forEach((timer) => {
        clearTimeout(timer);
      });
      quantityUpdateTimers.current = {};
    }

    if (window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }

    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfGenerated(false);
    setPdfGenerating(false);
    setPdfError(null);
    setPdfModalOpen(false);
    setPaymentModalOpen(false);
    setCompleteSaleLoading(false);
    setPaymentSuccessModalOpen(false);
    setIsClosePaymentDialogOpen(false);

    // Reset cart and products
    setCurrentProducts([]);
    setLocalQuantities({});
    setCart({
      id: null,
      customer_id: initData?.walk_in_customer?.id || null,
      store_id: currentStore,
      user_id: currentUser?.id || null,
      subtotal: 0,
      tax_amount: 0,
      discount_type: "none",
      discount_amount: 0,
      discount_value: 0,
      item_count: 0,
      total: 0,
      status: "active",
      notes: null,
      custom_fields: null,
      items: [],
    });

    // Reset customer to walk-in
    if (initData?.walk_in_customer) {
      setCustomer(initData.walk_in_customer);
    }

    // Clear Redux cart
    dispatch(setcurrent_cart({}));
    setInProgress(false);
    lastLoadedCartRef.current = null;

    // Reset current sale data
    setCurrentSaleData(null);

    // Reset payment data
    setPaymentData({
      sale_id: "",
      amount: 0,
      payment_method:
        availablePaymentMethods?.length > 0
          ? availablePaymentMethods[0].id || availablePaymentMethods[0]
          : "cash",
      transaction_id: "",
      payment_date: getCurrentDateInStoreTimezone(),
      authorization_code: "",
      notes: "",
      payment_status: "completed",
    });

    // Reset payments state
    setPayments([]);
    setPaidAmount(0);

    // Refresh products to ensure updated inventory and serial numbers
    if (activeTab === "all-products") {
      fetchProducts(null, selectedBrand, allProductsSearch, true, 1, true);
    } else if (currentCat) {
      fetchProducts(
        currentCat,
        selectedBrand,
        "",
        true,
        1,
        true,
      );
    } else {
      fetchProducts(null, selectedBrand, "", true, 1, true);
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
  };

  const handleCloseSuccessModal = () => {
    prepareNewSale();
  };

  const confirmClosePaymentModal = async () => {
    prepareNewSale();
  };

  const fetchRecentOrders = () => {
    setRecentOrdersModalOpen(true);
  };


  const selectOrder = async (orderId) => {
    try {
      // Show loading toast
      // sonnerToast.info("Loading order...");

      // If there's a current order in progress, put it on hold
      if (cart.id && cart.id !== orderId) {
        await updateCart({
          ...cart,
          status: "on-hold",
        });
      }

      // Fetch the selected cart
      const cartRes = await apiClient.get(
        API_ENDPOINTS.SALES.POS_CART_DETAILS(orderId),
      );

      if (cartRes.status === 200 || cartRes.status === 201) {
        const selectedCart = cartRes.data.data;
        activeCartSessionRef.current += 1;
        lastLoadedCartRef.current = String(orderId);
        setCart(selectedCart);
        dispatch(setcurrent_cart(selectedCart));
        setInProgress(true);

        // Fetch the customer for the selected cart
        if (selectedCart.customer_id) {
          await fetchCustomer(selectedCart.customer_id);
        }

        // Fetch cart items if not included in cart response
        if (Array.isArray(selectedCart.items) && selectedCart.items.length > 0) {
          setCurrentProducts(selectedCart.items);
        } else {
          const itemsRes = await apiClient.get(
            API_ENDPOINTS.SALES.POS_CART_ITEMS(orderId),
          );

          if (itemsRes.status === 200 || itemsRes.status === 201) {
            setCurrentProducts(itemsRes.data.data);
          }
        }

        setRecentOrdersModalOpen(false);
        showToast("Order loaded successfully", "success");
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to select order",
        "error",
      );
    }
  };

  const debouncedSearchCustomers = (searchTerm) => {
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    customerSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(searchTerm);
    }, 500);
  };

  const handleLoadSaleToPos = async (sale) => {
    if (!sale) return;
    try {
      sonnerToast.info(`Loading Sale #${sale.invoice_number || sale.id}...`);

      // 1. Fetch complete sale details to guarantee we have all items, customer, etc.
      let fullSale = sale;
      try {
        const saleDetailRes = await apiClient.get(
          API_ENDPOINTS.SALES.SALE_DETAILS(sale.id),
        );
        if (saleDetailRes.data?.data) {
          fullSale = saleDetailRes.data.data;
        } else if (saleDetailRes.data) {
          fullSale = saleDetailRes.data;
        }
      } catch (detailErr) {
        console.warn("Could not fetch full sale details, using provided sale object:", detailErr);
      }

      // 2. Set Customer in POS
      if (fullSale.customer) {
        setCustomer(fullSale.customer);
      } else if (fullSale.customer_id) {
        await fetchCustomer(fullSale.customer_id);
      } else if (initData?.walk_in_customer) {
        setCustomer(initData.walk_in_customer);
      }

      const cartId = `sale-${fullSale.id}`;

      // 3. Map sale items into POS cart items
      const rawItems =
        fullSale.items || fullSale.sale_items || fullSale.details || [];
      const mappedItems = [];

      for (let idx = 0; idx < rawItems.length; idx++) {
        const item = rawItems[idx];
        let pData = item.product_data;
        if (typeof pData === "string") {
          try {
            pData = JSON.parse(pData);
          } catch {
            pData = item.product || {};
          }
        } else if (!pData || typeof pData !== "object") {
          pData = item.product || {};
        }

        if (!pData.name && item.product?.name) {
          pData = { ...item.product, ...pData };
        }

        const productId = item.product_id || item.product?.id || pData.id || `p-${idx}`;
        const unitPrice = parseFloat(
          item.unit_price || item.original_price || pData.sell_price || 0,
        );
        const originalPrice = parseFloat(item.original_price || unitPrice);
        const qty = parseFloat(item.quantity || 1);
        const lineTotal = parseFloat(
          item.line_total != null ? item.line_total : qty * unitPrice,
        );

        if (!pData.name) {
          pData = {
            ...pData,
            id: productId,
            name:
              item.product_name ||
              item.name ||
              item.product?.name ||
              `Product #${productId}`,
            sku: item.sku || item.product?.sku || "",
            sell_price: unitPrice,
          };
        }

        let serials = [];
        if (Array.isArray(item.serial_numbers)) {
          serials = item.serial_numbers;
        } else if (
          typeof item.serial_numbers === "string" &&
          item.serial_numbers.startsWith("[")
        ) {
          try {
            serials = JSON.parse(item.serial_numbers);
          } catch {
            serials = [];
          }
        } else if (item.inventory_serials) {
          if (Array.isArray(item.inventory_serials)) {
            serials = item.inventory_serials.map((s) => s?.serial_number).filter(Boolean);
          } else if (item.inventory_serials?.serial_number) {
            serials = [item.inventory_serials.serial_number];
          }
        } else if (item.inventory_serial?.serial_number) {
          serials = [item.inventory_serial.serial_number];
        } else if (item.serial_number) {
          serials = [item.serial_number];
        }

        const cartItemPayload = {
          ...item,
          id: item.id || `sale-item-${fullSale.id}-${idx}`,
          sale_id: fullSale.id,
          sale_item_id: item.id,
          product_id: productId,
          cart_id: cartId,
          quantity: qty,
          original_price: originalPrice,
          unit_price: unitPrice,
          line_total: lineTotal,
          discount_amount: parseFloat(item.discount_amount || 0),
          discount_type: item.discount_type || "none",
          discount_value: parseFloat(item.discount_value || 0),
          tax_amount: parseFloat(item.tax_amount || 0),
          notes: item.notes || "",
          product_data: typeof pData === "string" ? pData : JSON.stringify(pData),
          product: pData,
          inventory_batch_id: item.inventory_batch_id || null,
          inventory_batch: item.inventory_batch || null,
          inventory_serials: item.inventory_serials || null,
          inventory_serial: item.inventory_serial || null,
          inventory_serial_id: item.inventory_serial_id || null,
          serial_numbers: serials,
          warranty_start_date: item.warranty_start_date || null,
          warranty_end_date: item.warranty_end_date || null,
          warranty_notes: item.warranty_notes || null,
          warranty_status: item.warranty_status || null,
          status: item.status || null,
          returned_quantity: item.returned_quantity != null ? item.returned_quantity : null,
        };

        mappedItems.push(cartItemPayload);
      }

      // 4. Update POS Cart & Items state IMMEDIATELY
      setCurrentProducts(mappedItems);

      const newLocalQuantities = {};
      mappedItems.forEach((i) => {
        newLocalQuantities[i.id] = i.quantity;
      });
      setLocalQuantities(newLocalQuantities);

      // 5. Update cart totals and sale tracking
      const subtotalVal = parseFloat(
        fullSale.subtotal ||
        fullSale.sub_total ||
        mappedItems.reduce((acc, i) => acc + (parseFloat(i.line_total) || 0), 0),
      );
      const totalVal = parseFloat(
        fullSale.total ||
        fullSale.total_amount ||
        mappedItems.reduce((acc, i) => acc + (parseFloat(i.line_total) || 0), 0),
      );

      setCart({
        id: cartId,
        sale_id: fullSale.id,
        customer_id: fullSale.customer_id || fullSale.customer?.id,
        discount_type: fullSale.discount_type || "none",
        discount_value: parseFloat(fullSale.discount_value || 0),
        discount_amount: parseFloat(fullSale.discount_amount || 0),
        subtotal: subtotalVal,
        total: totalVal,
        status: "active",
      });

      // 6. Track as current sale data for payment / checkout
      setCurrentSaleData(fullSale);
      let paid = 0;
      if (fullSale.paid_amount != null) {
        paid = parseFloat(fullSale.paid_amount || 0);
      } else if (Array.isArray(fullSale.payments) && fullSale.payments.length > 0) {
        paid = fullSale.payments.reduce((acc, p) => {
          const pStatus = (p.payment_status || "").toLowerCase();
          if (pStatus !== "failed" && pStatus !== "cancelled") {
            return acc + parseFloat(p.amount || 0);
          }
          return acc;
        }, 0);
      } else if (
        (fullSale.payment_status || "").toLowerCase() === "completed" ||
        (fullSale.payment_status || "").toLowerCase() === "paid" ||
        (fullSale.status || "").toLowerCase() === "completed"
      ) {
        paid = totalVal;
      }
      setPaidAmount(paid);

      const isCompleted =
        (fullSale.payment_status || "").toLowerCase() === "completed" ||
        (fullSale.payment_status || "").toLowerCase() === "paid";

      const balanceDue =
        isCompleted
          ? 0
          : fullSale.balance_due != null
            ? parseFloat(fullSale.balance_due)
            : Math.max(0, parseFloat((totalVal - paid).toFixed(2)));

      const defaultMethod =
        availablePaymentMethods?.length > 0
          ? availablePaymentMethods[0].id || availablePaymentMethods[0]
          : "cash";

      setPaymentData({
        sale_id: fullSale.id,
        amount: balanceDue > 0 ? parseFloat(balanceDue.toFixed(2)) : totalVal,
        payment_method: defaultMethod,
        transaction_id: `TXN-${Date.now()}`,
        payment_date: getCurrentDateInStoreTimezone(),
        notes: "",
        payment_status: "completed",
      });

      setInProgress(true);

      sonnerToast.success(
        `Sale #${fullSale.invoice_number || fullSale.id} loaded into POS with ${mappedItems.length} item(s)!`,
      );
    } catch (err) {
      console.error("Error loading sale into POS:", err);
      sonnerToast.error("Failed to load sale into POS");
    }
  };

  const handleOpenWarrantyClaimForItem = (item) => {
    if (!hasPermission("warranty claim create")) {
      sonnerToast.error(
        "You do not have permission ('warranty claim create') to create warranty claims",
      );
      return;
    }
    const targetItem =
      item ||
      currentProducts?.find((p) => !isItemWarrantyReturned(p)) ||
      currentProducts?.[0] ||
      null;
    setSelectedWarrantyItem(targetItem);
    setWarrantyClaimModalOpen(true);
  };

  const handleSelectSaleItemForWarranty = async (sale, item) => {
    if (!hasPermission("warranty claim create")) {
      sonnerToast.error(
        "You do not have permission ('warranty claim create') to create warranty claims",
      );
      return;
    }
    if (!currentSaleData || currentSaleData.id !== sale.id) {
      await handleLoadSaleToPos(sale);
    }
    const targetItem =
      item ||
      currentProducts?.find((p) => !isItemWarrantyReturned(p)) ||
      null;
    setSelectedWarrantyItem(targetItem);
    setWarrantyClaimModalOpen(true);
  };

  // Handle ?sale_id= in query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const saleIdParam = searchParams.get("sale_id");
    const warrantyParam = searchParams.get("warranty");
    const paymentParam = searchParams.get("payment");
    if (saleIdParam && init) {
      const fetchAndLoadSale = async () => {
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.SALES.SALE_DETAILS(saleIdParam),
          );
          if (res.data?.data) {
            await handleLoadSaleToPos(res.data.data);
            if (warrantyParam) {
              if (!hasPermission("warranty claim create")) {
                sonnerToast.error(
                  "You do not have permission ('warranty claim create') to create warranty claims",
                );
              } else {
                setWarrantyClaimModalOpen(true);
              }
            } else if (paymentParam) {
              setPaymentModalOpen(true);
            }
          }
        } catch (e) {
          console.warn("Could not load sale from URL param:", e);
        }
      };
      fetchAndLoadSale();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, init]);

  useEffect(() => {
    return () => {
      if (allProductsSearchTimeoutRef.current) {
        clearTimeout(allProductsSearchTimeoutRef.current);
      }
      if (categorySearchTimeoutRef.current) {
        clearTimeout(categorySearchTimeoutRef.current);
      }
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (cart.id && currentProducts.length > 0) {
      const timeoutId = setTimeout(() => {
        updateCartTotals(cart.id, currentProducts);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.discount_value, cart.discount_type]);

  const sendEmail = async (data) => {
    const saleCustomer = data?.customer || currentSaleData?.customer || customer;
    const email = saleCustomer?.email;
    if (!email || email === "" || email === "-" || !email.includes("@")) {
      showToast("Customer email is empty or invalid", "error");
      return;
    }
    const saleId = data?.id || currentSaleData?.id;
    if (!saleId) {
      showToast("Sale ID is missing", "error");
      return;
    }
    setButtonLoading(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.SALES.SEND_INVOICE(saleId),
        null,
      );
      showToast("Invoice email sent successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to send invoice email",
        "error",
      );
    } finally {
      setButtonLoading(false);
    }
  };

  const customerNameLabel = (customer) => {
    if (!customer) return "";
    const displayName =
      customer.customer_type !== "individual" && customer.company
        ? customer.company
        : customer.full_name;
    const labels = [displayName, customer.phone].filter(Boolean);
    return labels.join(" - ");
  };

  const handleCancelOrder = async () => {
    const cartId = cart?.id;
    if (cartId && !String(cartId).startsWith("sale-")) {
      try {
        const { payload } = formatCartPayload(cart, currentProducts, {
          id: cartId,
          status: "cancelled",
        });
        apiClient
          .put(API_ENDPOINTS.SALES.POS_CART_DETAILS(cartId), payload)
          .catch((err) => {
            console.error("Failed to cancel cart on backend:", err);
          });
      } catch (err) {
        console.error("Error formatting cancel cart payload:", err);
      }
    }
    showToast("Sale cancelled. POS ready for a new sale.", "info");
    prepareNewSale();
  };

  const handleHoldOrder = async () => {
    if (currentSaleData) {
      showToast("Cannot put a finalized sale on hold.", "warning");
      return;
    }
    const cartId = cart?.id;
    if (cartId && !String(cartId).startsWith("sale-")) {
      try {
        const { payload } = formatCartPayload(cart, currentProducts, {
          id: cartId,
          status: "on-hold",
        });
        await apiClient.put(
          API_ENDPOINTS.SALES.POS_CART_DETAILS(cartId),
          payload,
        );
        showToast("Order placed on hold", "info");
      } catch (err) {
        console.error("Failed to hold cart on backend:", err);
        showToast("Failed to place order on hold", "error");
        return;
      }
    }
    prepareNewSale();
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background relative">
      {/* Mobile Toggle Button */}
      <button
        style={{
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
        }}
        className="lg:hidden fixed left-0 top-1/2 -translate-y-1/2 z-[9999] rounded-r-2xl w-14 h-20 shadow-[4px_0_15px_rgba(0,0,0,0.2)] flex items-center justify-center scale-110 active:scale-95 transition-all border-y-2 border-r-2 border-white pl-1"
        onClick={() => setShowCartOnMobile(!showCartOnMobile)}
      >
        {showCartOnMobile ? (
          <LayoutGrid className="h-7 w-7 pointer-events-none" />
        ) : (
          <div className="relative pointer-events-none">
            <ShoppingCart className="h-7 w-7" />
            {(cart?.cart_items?.length > 0 || currentProducts?.length > 0) && (
              <span className="absolute -top-3 -right-3 bg-red-600 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                {(cart?.cart_items?.length || 0) +
                  (currentProducts?.length || 0)}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Main 1:1 Workspace */}
      <div className="flex-1 min-w-0 flex h-full overflow-hidden">
        {/* Left Side: Cart */}
        <div
          className={`${showCartOnMobile ? "flex" : "hidden"
            } lg:flex w-full lg:w-1/2 flex-1 min-w-0 h-full border-r border-border bg-background shadow-sm z-10 flex-col`}
        >
          <CartSidebar
            customer={customer}
            customerSearchExpanded={customerSearchExpanded}
            setCustomerSearchExpanded={setCustomerSearchExpanded}
            customerLoading={customerLoading}
            customers={customers}
            customerNameLabel={customerNameLabel}
            debouncedSearchCustomers={debouncedSearchCustomers}
            cart={cart}
            setCart={setCart}
            fetchCustomer={fetchCustomer}
            updateCart={updateCart}
            hasPermission={hasPermission}
            setModalOpen={setModalOpen}
            searchInputRef={searchInputRef}
            searchDropdownProducts={searchDropdownProducts}
            showToast={showToast}
            setFilterModalOpen={setFilterModalOpen}
            selectedBrand={selectedBrand}
            priceFilter={priceFilter}
            currentProducts={currentProducts}
            clearAllItems={clearAllItems}
            currencySymbol={currencySymbol}
            localQuantities={localQuantities}
            handleQuantityChange={handleQuantityChange}
            updatingItemIds={updatingItemIds}
            handleEditCartItem={handleEditCartItem}
            removeCartItem={removeCartItem}
            calculateSubTotal={calculateSubTotal}
            calculateTotal={calculateTotal}
            qtyUpdating={qtyUpdating}
            fetchRecentOrders={fetchRecentOrders}
            onCancelOrder={currentSaleData ? prepareNewSale : handleCancelOrder}
            onHoldOrder={handleHoldOrder}
            completing={completing}
            completeOrder={handleCheckout}
            currentSaleData={currentSaleData}
            paidAmount={paidAmount}
            balanceDue={balanceDue}
            onExitSale={prepareNewSale}
            onClaimWarranty={handleOpenWarrantyClaimForItem}
            addCartItem={addCartItem}
          />
        </div>

        {/* Right Side: Products */}
        <div
          className={`${!showCartOnMobile ? "flex" : "hidden"
            } lg:flex w-full lg:w-1/2 flex-1 min-w-0 h-full overflow-hidden flex-col bg-background`}
        >
          <ProductView
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            hasPermission={hasPermission}
            currentCat={currentCat}
            currentCatName={currentCatName}
            categorySearch={categorySearch}
            onCategorySearchChange={handleCategorySearchChange}
            categoriesLoading={categoriesLoading}
            categories={categories}
            subcategories={subcategories}
            subcategoriesLoading={subcategoriesLoading}
            handleCategorySelect={handleCategorySelect}
            selectedBrand={selectedBrand}
            brands={brands}
            allProductsSearch={allProductsSearch}
            onAllProductsSearchChange={handleAllProductsSearchChange}
            products={products}
            productsLoading={productsLoading}
            isFetchingMore={isFetchingMore}
            productsError={productsError}
            filterProductsByPrice={filterProductsByPrice}
            addCartItem={addCartItem}
            currencySymbol={currencySymbol}
            recentProducts={recentProducts}
            hasMoreProducts={hasMoreProducts}
            onLoadMore={handleLoadMoreProducts}
            onCancelOrder={currentSaleData ? prepareNewSale : handleCancelOrder}
            onRecentOrders={fetchRecentOrders}
            onHoldOrder={handleHoldOrder}
            onCompleteOrder={handleCheckout}
            completing={completing}
            currentSaleData={currentSaleData}
            balanceDue={balanceDue}
            onExitSale={prepareNewSale}
          />
        </div>
      </div>

      {/* Right Side: Actions Sidebar */}
      <PosActionsSidebar
        mode="pos"
        currentProducts={currentProducts}
        customer={customer}
        currencySymbol={currencySymbol}
        onLoadSaleToCart={handleLoadSaleToPos}
        onClearCart={clearAllItems}
        currentSaleData={currentSaleData}
        onSelectSaleItemForWarranty={handleSelectSaleItemForWarranty}
      />

      {/* Modals */}
      <CustomerModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSuccess={(createdCustomer) => {
          const updatedCart = {
            ...cart,
            customer_id: createdCustomer.id,
          };
          setCart(updatedCart);
          setCustomer(createdCustomer);

          if (cart.id) {
            updateCart(updatedCart);
          }
        }}
      />

      <PaymentModal
        show={paymentModalOpen}
        onHide={handleClosePaymentModal}
        currencySymbol={currencySymbol}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        processPayment={processPayment}
        paymentProcessing={paymentProcessing}
        totalAmount={currentSaleData?.total ? parseFloat(currentSaleData.total) : calculateTotal()}
        paidAmount={paidAmount}
        payments={payments}
        hasPermission={hasPermission}
        availablePaymentMethods={availablePaymentMethods}
        onPayLater={handlePayLater}
        payLaterLoading={payLaterLoading}
        onCompleteSaleWithoutPayment={handleCompleteSaleWithoutPayment}
        completeSaleLoading={completeSaleLoading}
        onPrintInvoice={handlePrintInvoice}
        onPrintReceipt={handlePrintReceipt}
        invoiceLoading={invoiceLoading}
        receiptGenerating={receiptGenerating}
        currentSaleData={currentSaleData}
        customer={customer}
        onSendEmail={sendEmail}
        emailSending={buttonLoading}
        onNewSale={prepareNewSale}
      />

      {/* Invoice PDF Modal */}
      <Dialog open={pdfModalOpen} onOpenChange={handleClosePDFModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice PDF
            </DialogTitle>
            <DialogDescription>
              Generate, download, print, or email the invoice for this sale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {currentSaleData && (
              <div className="bg-muted p-4 rounded-lg">
                <h6 className="font-semibold mb-2">
                  Sale #{currentSaleData.invoice_number || currentSaleData.id}
                </h6>
                <p className="text-muted-foreground text-sm">
                  <strong>Total:</strong> {currencySymbol}
                  {parseFloat(currentSaleData.total || 0).toFixed(2)}
                </p>
              </div>
            )}

            {pdfGenerating && (
              <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 p-4 rounded-lg flex items-center border border-blue-200 dark:border-blue-800">
                <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                <span>Generating PDF invoice...</span>
              </div>
            )}

            {pdfGenerated && !pdfGenerating && (
              <div>
                <div className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 p-4 rounded-lg flex items-center border border-green-200 dark:border-green-800 mb-4">
                  <FileText className="mr-2 h-4 w-4" />
                  PDF invoice generated successfully!
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button className="w-full" onClick={downloadPDF}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={printPDF}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Print PDF
                  </Button>
                  {hasPermission("report sales receipt view") && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        handleGenerateReceiptReport(currentSaleData?.id)
                      }
                      disabled={receiptGenerating}
                      className="w-full gap-2 font-bold col-span-2"
                    >
                      {receiptGenerating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Printer className="h-4 w-4" />
                      )}
                      Print Thermal Receipt
                    </Button>
                  )}
                  {currentSaleData &&
                    (currentSaleData.customer?.email || customer?.email) &&
                    (currentSaleData.customer?.email || customer?.email) !== "" &&
                    (currentSaleData.customer?.email || customer?.email) !== "-" && (
                      <Button
                        className="w-full col-span-2"
                        variant="outline"
                        onClick={() => sendEmail(currentSaleData)}
                        disabled={buttonLoading}
                      >
                        {buttonLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending Invoice...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Invoice Email
                          </>
                        )}
                      </Button>
                    )}
                </div>
              </div>
            )}

            {pdfError && !pdfGenerating && !pdfGenerated && (
              <div className="bg-destructive/10 text-destructive p-4 rounded-lg border border-destructive/20">
                <div className="flex items-center">
                  <X className="mr-2 h-4 w-4" />
                  PDF generation failed. Please try again.
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPdfError(null);
                      if (currentSaleData?.id) {
                        handleGeneratePDFReport(currentSaleData.id);
                      }
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                  {currentSaleData &&
                    (currentSaleData.customer?.email || customer?.email) &&
                    (currentSaleData.customer?.email || customer?.email) !== "" &&
                    (currentSaleData.customer?.email || customer?.email) !== "-" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => sendEmail(currentSaleData)}
                        disabled={buttonLoading}
                        className="gap-1.5 font-bold"
                      >
                        {buttonLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="mr-2 h-4 w-4" />
                        )}
                        Send Invoice Email
                      </Button>
                    )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RecentOrdersModal
        show={recentOrdersModalOpen}
        onHide={() => setRecentOrdersModalOpen(false)}
        currencySymbol={currencySymbol}
        selectOrder={selectOrder}
        storeId={currentStore}
        currentCartId={cart?.id}
      />

      <PaymentSuccessModal
        show={paymentSuccessModalOpen}
        onHide={handleCloseSuccessModal}
        cart={cart}
        currencySymbol={currencySymbol}
        pdfGenerating={pdfGenerating}
        pdfGenerated={pdfGenerated}
        pdfBlobUrl={pdfBlobUrl}
        currentSaleData={currentSaleData}
        pdfError={pdfError}
        buttonLoading={buttonLoading}
        downloadPDF={downloadPDF}
        printPDF={printPDF}
        sendEmail={sendEmail}
        generateReceipt={handleGenerateReceiptReport}
        receiptGenerating={receiptGenerating}
      />

      <FilterModal
        show={filterModalOpen}
        onHide={() => setFilterModalOpen(false)}
        brands={brands}
        selectedBrand={selectedBrand}
        handleBrandChange={handleBrandChange}
        currencySymbol={currencySymbol}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        clearFilters={clearFilters}
        applyFilters={applyFilters}
      />

      {/* Toast Notification */}
      {/* Old toast removed in favor of sonner */}

      <ItemConfigModal
        show={showItemModal}
        onHide={() => {
          setShowItemModal(false);
          setEditingCartItem(null);
          setSelectedModalProduct(null);
          setCurrentItemConfig({
            product_id: null,
            cart_id: null,
            quantity: 1,
            original_price: 0,
            unit_price: 0,
            line_total: 0,
            discount_amount: 0,
            discount_type: "none",
            discount_value: 0,
            tax_amount: 0,
            tax_id: null,
            notes: "",
            product_data: null,
            serial_numbers: [],
          });
        }}
        editingCartItem={editingCartItem}
        selectedModalProduct={selectedModalProduct}
        currencySymbol={currencySymbol}
        currentItemConfig={currentItemConfig}
        setCurrentItemConfig={setCurrentItemConfig}
        discountTypeOptions={discountTypeOptions}
        taxOptions={taxOptions}
        taxes={taxes}
        availableSerials={availableSerials}
        availableBatches={availableBatches}
        calculateItemConfigTotal={calculateItemConfigTotal}
        handleSaveItemConfig={handleSaveItemConfig}
      />

      <InventorySelectorModal
        show={showInventorySelector}
        onHide={() => setShowInventorySelector(false)}
        type={inventorySelectorType}
        availableItems={inventorySelectorItems}
        selectedItems={
          inventorySelectorType === "standard"
            ? currentItemConfig.inventory_batch_id
              ? [currentItemConfig.inventory_batch_id]
              : []
            : currentItemConfig.serial_numbers || []
        }
        onSelect={handleInventorySelected}
        quantity={inventorySelectorQuantity}
      />

      <PinVerificationModal
        show={pinModalOpen}
        onHide={() => setPinModalOpen(false)}
        pinVerifying={pinVerifying}
        pinActionTitle={pinActionTitle}
        handleVerifyPin={handleVerifyPin}
        pinValue={pinValue}
        setPinValue={setPinValue}
      />

      <AlertDialog
        open={isClearCartDialogOpen}
        onOpenChange={setIsClearCartDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all items?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all products from the current cart.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmClearAllItems}
            >
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isClosePaymentDialogOpen}
        onOpenChange={setIsClosePaymentDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Payment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the payment? This will clear the
              current POS data and prepare for a new sale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmClosePaymentModal}
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {warrantyClaimModalOpen && hasPermission("warranty claim create") && (
        <PosCreateWarrantyClaimModal
          show={warrantyClaimModalOpen}
          onHide={() => {
            setWarrantyClaimModalOpen(false);
            setSelectedWarrantyItem(null);
          }}
          sale={currentSaleData}
          selectedItem={selectedWarrantyItem}
          saleItems={currentProducts}
          currencySymbol={currencySymbol}
        />
      )}
    </div>
  );
};

export default Pos;
